import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import TrDateInput from '../components/TrDateInput';
import { useTranslation } from 'react-i18next';

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

interface Proje {
    projeID: number;
    projeAdi: string;
}

interface CariRecord {
    cariID: number;
    adiSoyadi: string;
    firmaAdi: string | null;
    turu: string;
}

interface KasaRecord {
    kasaID: number;
    kasaAdi: string;
    kasaTuru: string;
}

interface OdemeRecord {
    odemeID: number;
    cariID: number;
    islemTarihi: string;
    vadeTarihi: string | null;
    tahsilatTarihi: string | null;
    miktar: number;
    paraBirimiID: number;
    islemTakipNo: string;
    odemeTuru: string;
    odemeYonu: string;
    odemeDurumu: string;
    aciklama: string | null;
    hizmetUrunAdi: string | null;
    aktifMi: boolean;
    sozlesmeOdemesi: boolean;
    projeID: number | null;
    kasaID: number | null;
    tur?: string;
    islemTipi?: string;
    cari: { cariID: number; adiSoyadi: string; telefon: string | null; firmaAdi: string | null } | null;
    paraBirimi: { paraBirimiID: number; birim: string; carpan: number } | null;
    ilgiliProje: { projeID: number; projeAdi: string } | null;
    sozlesme: { sozlesmeID: number; sozlesmeTakipNo: string; projeID: number | null; projeAdi: string | null; daireNo: string | null } | null;
    kasa: { kasaID: number; kasaAdi: string; kasaTuru: string } | null;
}

interface KasaHareketRecord {
    hareketID: number;
    kasaID: number;
    islemTuru: string; // "Giris" | "Cikis"
    miktar: number;
    tarih: string;
    projeID: number | null;
    cariID: number | null;
    odemeID: number | null;
    kategoriID: number | null;
    aciklama: string | null;
    kasa: { kasaID: number; kasaAdi: string; kasaTuru: string } | null;
    cari: { cariID: number; adiSoyadi: string } | null;
    kategori: { kategoriID: number; kategoriAdi: string; tur: string } | null;
}

interface EkstreSozlesme {
    sozlesmeID: number;
    sozlesmeTakipNo: string;
    sozlesmeTarihi: string;
    sozlesmeDurumu: string;
    sozlesmeFiyati: number;
    aktifMi: boolean;
    daire: {
        daireID: number;
        daireNo: string;
        blok?: {
            blokAdi: string;
            proje?: { projeID: number; projeAdi: string };
        };
    } | null;
}

interface SozlesmeRaporRecord {
    sozlesmeID: number;
    sozlesmeFiyati: number;
    sozlesmeDurumu: string;
    cari: { cariID: number; adiSoyadi: string } | null;
    daire: {
        daireID: number;
        daireNo: string;
        blok: {
            blokAdi: string;
            proje: { projeID: number; projeAdi: string } | null;
        } | null;
    } | null;
}

interface RaporItemDef {
    id: string; title: string; desc: string; category: string; isNew?: boolean;
    newBadgeStyle?: React.CSSProperties; iconBg: string; iconBorder: string;
    icon: React.ReactNode; accentColor: string; accentShadow: string; onClick: () => void;
}

const RaporKartItem: React.FC<{
    r: RaporItemDef;
    isFavori: boolean;
    onToggleFavori: (e: React.MouseEvent, id: string) => void;
    cardStyle: React.CSSProperties;
}> = ({ r, isFavori, onToggleFavori, cardStyle }) => {
    const { t } = useTranslation();
    const [hovered, setHovered] = React.useState(false);
    return (
        <div
            className="rapor-hub-card"
            onClick={r.onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                ...cardStyle,
                boxShadow: hovered ? `0 4px 16px ${r.accentShadow}` : '0 1px 3px rgba(0,0,0,0.04)',
                borderColor: hovered ? r.accentColor : '#e2e8f0',
                transform: hovered ? 'translateY(-2px)' : 'none',
            }}
        >
            <div style={{ width: 48, height: 48, borderRadius: 12, background: r.iconBg, border: `1px solid ${r.iconBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {r.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {r.title}
                    {r.isNew && (
                        <span style={{ fontSize: '0.7rem', padding: '1px 7px', borderRadius: 10, fontWeight: 600, ...(r.newBadgeStyle || { background: '#f3e8ff', color: '#7c3aed', border: '1px solid #e9d5ff' }) }}>{t('badge.yeni', 'Yeni')}</span>
                    )}
                </h4>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.82rem' }}>{r.desc}</p>
            </div>
            <button
                className={`rapor-star-btn${isFavori ? ' active' : ''}`}
                onClick={(e) => onToggleFavori(e, r.id)}
                title={isFavori ? t('rapor.favorilerdenCikar') : t('rapor.favorilereEkle')}
            >
                <svg width="17" height="17" viewBox="0 0 24 24" fill={isFavori ? '#f59e0b' : 'none'} stroke={isFavori ? '#f59e0b' : 'currentColor'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            </button>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke={hovered ? r.accentColor : '#94a3b8'} width={20} height={20} style={{ flexShrink: 0, transition: 'stroke 0.2s ease' }}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
        </div>
    );
};

// Yerel saat dilimiyle YYYY-MM-DD formatı (toISOString UTC hatasından kaçınmak için)
const toLocalDateStr = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const Raporlar: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [allOdemeler, setAllOdemeler] = useState<OdemeRecord[]>([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [raporReady, setRaporReady] = useState(false);

    // Filtreler
    const [selectedProjeID, setSelectedProjeID] = useState<string>('');
    const [modalBaslangic, setModalBaslangic] = useState(() => {
        const d = new Date();
        d.setMonth(0, 1);
        return toLocalDateStr(d);
    });
    const [modalBitis, setModalBitis] = useState(() => {
        const d = new Date();
        d.setMonth(11, 31);
        return toLocalDateStr(d);
    });
    const [modalBaslangicDisplay, setModalBaslangicDisplay] = useState(() => {
        const d = new Date();
        d.setMonth(0, 1);
        return toLocalDateStr(d).split('-').reverse().join('.');
    });
    const [modalBitisDisplay, setModalBitisDisplay] = useState(() => {
        const d = new Date();
        d.setMonth(11, 31);
        return toLocalDateStr(d).split('-').reverse().join('.');
    });
    const [modalTarihTumu, setModalTarihTumu] = useState(false);
    const [yonFiltre, setYonFiltre] = useState<'Tümü' | 'Gelir' | 'Gider'>('Tümü');

    // Sayfalama
    const [gelirPage, setGelirPage] = useState(1);
    const [giderPage, setGiderPage] = useState(1);
    const [sozlesmePage, setSozlesmePage] = useState(1);
    const perPage = 10;

    // Proje Gelir-Gider raporu sözleşmeler
    const [sozlesmeler, setSozlesmeler] = useState<SozlesmeRaporRecord[]>([]);

    // === Cari Hesap Ekstresi State ===
    const [showEkstreModal, setShowEkstreModal] = useState(false);
    const [ekstreLoading, setEkstreLoading] = useState(false);
    const [ekstreReady, setEkstreReady] = useState(false);
    const [ekstreOdemeler, setEkstreOdemeler] = useState<OdemeRecord[]>([]);
    const [cariler, setCariler] = useState<CariRecord[]>([]);
    const [selectedCariID, setSelectedCariID] = useState<string>('');
    const [cariSearch, setCariSearch] = useState('');
    const [showCariDropdown, setShowCariDropdown] = useState(false);
    const [ekstreBaslangic, setEkstreBaslangic] = useState(() => {
        const d = new Date();
        d.setMonth(0, 1);
        return toLocalDateStr(d);
    });
    const [ekstreBitis, setEkstreBitis] = useState(() => {
        const d = new Date();
        d.setMonth(11, 31);
        return toLocalDateStr(d);
    });
    const [ekstrePage, setEkstrePage] = useState(1);
    const ekstrePerPage = 15;
    const [ekstreKartTipi, setEkstreKartTipi] = useState<'Müşteri' | 'Tedarikçi'>('Müşteri');
    // CariDetay ile aynı: taşeron sözleşmeler ayrı endpoint'ten
    const [ekstreSozlesmeler, setEkstreSozlesmeler] = useState<EkstreSozlesme[]>([]);

    // === Kasa ve Banka Ekstresi State ===
    const [showKasaEkstreModal, setShowKasaEkstreModal] = useState(false);
    const [kasaEkstreLoading, setKasaEkstreLoading] = useState(false);
    const [kasaEkstreReady, setKasaEkstreReady] = useState(false);
    const [kasaEkstreOdemeler, setKasaEkstreOdemeler] = useState<KasaHareketRecord[]>([]);
    const [kasalar, setKasalar] = useState<KasaRecord[]>([]);
    const [selectedKasaID, setSelectedKasaID] = useState<string>('');
    const [kasaSearch, setKasaSearch] = useState('');
    const [showKasaDropdown, setShowKasaDropdown] = useState(false);
    const [kasaEkstreBaslangic, setKasaEkstreBaslangic] = useState(() => { const d = new Date(); d.setMonth(0, 1); return toLocalDateStr(d); });
    const [kasaEkstreBitis, setKasaEkstreBitis] = useState(() => { const d = new Date(); d.setMonth(11, 31); return toLocalDateStr(d); });
    const [kasaEkstrePage, setKasaEkstrePage] = useState(1);
    const kasaEkstrePerPage = 15;
    // === Reporting Hub State ===
    const [hubMetrics, setHubMetrics] = useState({ kasaToplam: 0, bekleyenBuAy: 0, gecikenOdeme: 0, loading: true });
    const [activeCategory, setActiveCategory] = useState<'Tümü' | 'Finansal' | 'Stok' | 'Performans'>('Tümü');
    const [searchQuery, setSearchQuery] = useState('');
    const [favoriRaporlar, setFavoriRaporlar] = useState<string[]>(() => {
        try { return JSON.parse(localStorage.getItem('favoriRaporlar') || '[]'); } catch { return []; }
    });

    useEffect(() => {
        fetchProjeler();
        fetchHubMetrics();
    }, []);

    const fetchProjeler = async () => {
        try {
            const response = await axiosInstance.get('/api/Projeler');
            setProjeler(response.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const fetchHubMetrics = async () => {
        try {
            const [dashRes, odemeRes] = await Promise.all([
                axiosInstance.get('/api/KasaHareketleri/dashboard'),
                axiosInstance.get('/api/Odemeler', { params: { aktifMi: true } }),
            ]);
            const kasaToplam = dashRes.data.genelToplam || 0;
            const odemeler: OdemeRecord[] = odemeRes.data;
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const buAyBaslangic = new Date(today.getFullYear(), today.getMonth(), 1);
            const buAySon = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            buAySon.setHours(23, 59, 59, 999);
            let bekleyenBuAy = 0;
            let gecikenOdeme = 0;
            for (const o of odemeler) {
                if (!o.aktifMi || o.odemeDurumu === 'Odendi' || o.odemeYonu !== 'Gelir') continue;
                const vade = o.vadeTarihi ? new Date(o.vadeTarihi) : null;
                if (vade) {
                    if (vade >= buAyBaslangic && vade <= buAySon) bekleyenBuAy += o.miktar;
                    if (vade < today) gecikenOdeme += o.miktar;
                }
            }
            setHubMetrics({ kasaToplam, bekleyenBuAy, gecikenOdeme, loading: false });
        } catch {
            setHubMetrics(m => ({ ...m, loading: false }));
        }
    };

    const toggleFavori = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        setFavoriRaporlar(prev => {
            const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
            localStorage.setItem('favoriRaporlar', JSON.stringify(next));
            return next;
        });
    };

    const fetchCariler = async (tur: string) => {
        try {
            const response = await axiosInstance.get('/api/Cariler', { params: { tur } });
            setCariler(response.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleOpenEkstreModal = () => {
        setShowEkstreModal(true);
        setEkstreReady(false);
        setEkstreOdemeler([]);
        setSelectedCariID('');
        setEkstreKartTipi('Müşteri');
        setEkstrePage(1);
        fetchCariler('Müşteri');
    };

    const handleEkstreKartTipiChange = (tipi: 'Müşteri' | 'Tedarikçi') => {
        setEkstreKartTipi(tipi);
        setSelectedCariID('');
        setCariler([]);
        setEkstreReady(false);
        setEkstreOdemeler([]);
        setEkstreSozlesmeler([]);
        // DB'de Tedarikçi kayıtları Turu='Cari', Müşteri kayıtları Turu='Müşteri'
        fetchCariler(tipi === t('cari.tedarikci') ? 'Cari' : 'Müşteri');
    };

    const handleEkstreOlustur = async () => {
        setEkstreLoading(true);
        try {
            // CariDetay ile aynı: odemeler + sözleşmeler (hem Tedarikçi hem Müşteri için) ayrı endpoint'ten paralel çek
            const promises: Promise<any>[] = [
                axiosInstance.get('/api/Odemeler', { params: { aktifMi: true, cariID: selectedCariID || undefined } })
            ];
            if (selectedCariID) {
                promises.push(
                    axiosInstance.get('/api/Sozlesmeler', { params: { cariID: selectedCariID, aktifMi: true } })
                );
            }
            const results = await Promise.all(promises);
            setEkstreOdemeler(results[0].data.map((o: any) => ({ ...o, tur: o.tur || o.islemTipi })));
            setEkstreSozlesmeler(results[1]?.data ?? []);
            setEkstreReady(true);
            setEkstrePage(1);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setEkstreLoading(false);
        }
    };

    const handleEkstreTemizle = () => {
        setSelectedCariID('');
        const s = new Date(); s.setMonth(0, 1);
        const e = new Date(); e.setMonth(11, 31);
        const baslangicIso = toLocalDateStr(s);
        const bitisIso = toLocalDateStr(e);
        setEkstreBaslangic(baslangicIso);
        setEkstreBitis(bitisIso);
        setEkstreReady(false);
        setEkstreOdemeler([]);
        setEkstreSozlesmeler([]);
        setEkstrePage(1);
    };

    // Ekstre filtreleme — tur alanına göre (CariDetay/MusteriDetay ile aynı kural)
    const isBarterSozlesmeOdeme = (o: OdemeRecord) => !!(o.islemTakipNo?.startsWith('ODE-SOZ-'));

    const filteredEkstre = ekstreOdemeler
        .filter(o => {
            // Barter sözleşme ödemeleri ve otomatik sözleşme gelir kayıtları hariç
            if (isBarterSozlesmeOdeme(o)) return false;
            if (o.islemTakipNo?.startsWith('GEL-SOZ-')) return false;
            // Bekliyor olan Tahsilat/Ödeme kayıtları ekstre'de gizlenir
            const tur = o.tur || '';
            if ((tur === 'Tahsilat' || tur === 'Odeme') && o.odemeDurumu === 'Bekliyor') return false;
            if (selectedCariID && o.cariID !== parseInt(selectedCariID)) return false;
            const tarih = new Date(o.vadeTarihi || o.islemTarihi);
            const baslangic = new Date(ekstreBaslangic);
            const bitis = new Date(ekstreBitis);
            baslangic.setHours(0, 0, 0, 0);
            bitis.setHours(23, 59, 59, 999);
            if (tarih < baslangic || tarih > bitis) return false;
            return true;
        })
        .sort((a, b) => new Date(a.vadeTarihi || a.islemTarihi).getTime() - new Date(b.vadeTarihi || b.islemTarihi).getTime());

    // tur alanına göre toplam hesabı (CariDetay/MusteriDetay ile aynı kural):
    // Gider → Alacak | Gelir → Borç | Tahsilat+Odendi → Alacak | Ödeme+Odendi → Borç
    const sozlesmeDurumuFilter = ekstreKartTipi === 'Tedarikçi' ? 'Taşeron' : 'Sözleşme';
    const toplamBorc =
        ekstreSozlesmeler.filter(s => s.sozlesmeDurumu === sozlesmeDurumuFilter).reduce((s, x) => s + x.sozlesmeFiyati, 0)
        + filteredEkstre.filter(o => o.tur === 'Gelir' || (o.tur === 'Odeme' && o.odemeDurumu === 'Odendi')).reduce((s, o) => s + o.miktar, 0);
    const toplamAlacak =
        filteredEkstre.filter(o => o.tur === 'Gider' || (o.tur === 'Tahsilat' && o.odemeDurumu === 'Odendi')).reduce((s, o) => s + o.miktar, 0);
    const kalanBakiye = Math.abs(toplamBorc - toplamAlacak);
    const kalanBaYonu = toplamBorc > toplamAlacak ? 'B' : toplamAlacak > toplamBorc ? 'A' : '';

    // tur alanına göre satır üretimi
    const ekstreWithBakiye = (() => {
        let cumBorc = 0, cumAlacak = 0;
        const rows: any[] = [];
        const mkBakiye = (b: number, a: number) => ({ bak: Math.abs(b - a), baYonu: b > a ? 'B' : a > b ? 'A' : '' });

        // Faz 1: Sözleşme satırları → Borç
        ekstreSozlesmeler
            .filter(s => s.sozlesmeDurumu === sozlesmeDurumuFilter)
            .forEach(s => {
                cumBorc += s.sozlesmeFiyati;
                const { bak, baYonu } = mkBakiye(cumBorc, cumAlacak);
                const projeAdi = s.daire?.blok?.proje?.projeAdi;
                const blokAdi = s.daire?.blok?.blokAdi;
                const daireNo = s.daire?.daireNo;
                const aciklama = [ekstreKartTipi === 'Tedarikçi' ? t('durum.taseronSozlesmesi', 'Taşeron Sözleşmesi') : t('durum.satisSozlesmesi', 'Satış Sözleşmesi'), projeAdi, blokAdi && daireNo ? `${blokAdi}-${daireNo}` : daireNo, s.sozlesmeTakipNo].filter(Boolean).join(' · ');
                rows.push({
                    rowEtiket: t('durum.sozlesme'), rowKey: `sozlesme-${s.sozlesmeID}`,
                    rowBorc: s.sozlesmeFiyati, rowAlacak: null,
                    bakiye: bak, baYonu,
                    islemTarihi: s.sozlesmeTarihi, vadeTarihi: null,
                    aciklama, hizmetUrunAdi: null,
                    cari: null, paraBirimi: null,
                    odemeDurumu: ekstreKartTipi === 'Müşteri' ? t('durum.sozlesme') : t('durum.taseron')
                });
            });

        // Faz 2: Ödemeler — tur alanına göre
        filteredEkstre.forEach(o => {
            const tur = o.tur || '';
            if (tur === 'Gider') {
                // Gider → Alacak
                cumAlacak += o.miktar;
                const { bak, baYonu } = mkBakiye(cumBorc, cumAlacak);
                rows.push({ ...o, rowBorc: null, rowAlacak: o.miktar, bakiye: bak, baYonu, rowEtiket: 'Gider', rowKey: `${o.odemeID}-gider` });
            } else if (tur === 'Gelir') {
                // Gelir → Borç
                cumBorc += o.miktar;
                const { bak, baYonu } = mkBakiye(cumBorc, cumAlacak);
                rows.push({ ...o, rowBorc: o.miktar, rowAlacak: null, bakiye: bak, baYonu, rowEtiket: 'Gelir', rowKey: `${o.odemeID}-gelir` });
            } else if (tur === 'Tahsilat' && o.odemeDurumu === 'Odendi') {
                // Tahsilat + Odendi → Alacak
                cumAlacak += o.miktar;
                const { bak, baYonu } = mkBakiye(cumBorc, cumAlacak);
                rows.push({ ...o, rowBorc: null, rowAlacak: o.miktar, bakiye: bak, baYonu, rowEtiket: 'Tahsilat', rowKey: `${o.odemeID}-tahsilat` });
            } else if (tur === 'Odeme' && o.odemeDurumu === 'Odendi') {
                // Ödeme + Odendi → Borç
                cumBorc += o.miktar;
                const { bak, baYonu } = mkBakiye(cumBorc, cumAlacak);
                rows.push({ ...o, rowBorc: o.miktar, rowAlacak: null, bakiye: bak, baYonu, rowEtiket: 'Ödeme', rowKey: `${o.odemeID}-odeme` });
            }
        });

        return rows;
    })();

    const ekstreTotalPages = Math.ceil(ekstreWithBakiye.length / ekstrePerPage);
    const paginatedEkstre = ekstreWithBakiye.slice((ekstrePage - 1) * ekstrePerPage, ekstrePage * ekstrePerPage);

    // === Kasa ve Banka Ekstresi Computed ===
    const filteredKasaEkstre = kasaEkstreOdemeler.filter(o => {
        const tarih = new Date(o.tarih);
        const baslangic = new Date(kasaEkstreBaslangic); baslangic.setHours(0, 0, 0, 0);
        const bitis = new Date(kasaEkstreBitis); bitis.setHours(23, 59, 59, 999);
        if (tarih < baslangic || tarih > bitis) return false;
        return true;
    }).sort((a, b) => new Date(a.tarih).getTime() - new Date(b.tarih).getTime());

    const kasaEkstreWithBakiye = (() => {
        let bakiye = 0;
        return filteredKasaEkstre.map(o => {
            const isGiris = o.islemTuru === 'Giris';
            if (isGiris) bakiye += o.miktar; else bakiye -= o.miktar;
            return { ...o, rowGiris: isGiris ? o.miktar : null, rowCikis: !isGiris ? o.miktar : null, bakiye };
        });
    })();
    const kasaToplamGiris = filteredKasaEkstre.filter(o => o.islemTuru === 'Giris').reduce((s, o) => s + o.miktar, 0);
    const kasaToplamCikis = filteredKasaEkstre.filter(o => o.islemTuru !== 'Giris').reduce((s, o) => s + o.miktar, 0);
    const kasaNetBakiye = kasaToplamGiris - kasaToplamCikis;
    const handleEkstrePdfExport = async () => {
        const selectedCari = cariler.find(c => c.cariID === parseInt(selectedCariID));

        const container = document.createElement('div');
        container.style.cssText = 'position:fixed;left:-9999px;top:0;width:1140px;font-family:Arial,sans-serif;background:#fff;padding:24px;box-sizing:border-box;';

        const rows = ekstreWithBakiye.map((o, i) => `
            <tr style="background:${o.rowEtiket === 'Ödeme Makbuzu' || o.rowEtiket === 'Tahsilat Makbuzu' ? '#f0fdf4' : (i % 2 === 0 ? '#ffffff' : '#f0f3f8')};">
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;">${new Date(o.islemTarihi).toLocaleDateString('tr-TR')}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;">${o.vadeTarihi ? new Date(o.vadeTarihi).toLocaleDateString('tr-TR') : '—'}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;">${o.cari?.adiSoyadi || '-'}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;"><strong>[${o.rowEtiket}]</strong> ${o.aciklama || ''}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;">${o.hizmetUrunAdi || '-'}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;text-align:right;">${o.rowBorc != null ? formatCurrency(o.rowBorc) : '-'}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;text-align:right;">${o.rowAlacak != null ? formatCurrency(o.rowAlacak) : '-'}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;text-align:right;">${formatCurrency(Math.abs(o.bakiye))}</td>
            </tr>
        `).join('');

        container.innerHTML = `
            <div style="margin-bottom:20px;border-bottom:3px solid #e67d21;padding-bottom:12px;">
                <h2 style="color:#1a2440;font-size:22px;margin:0 0 6px 0;font-weight:bold;">Cari Hesap Ekstresi</h2>
                <p style="margin:2px 0;color:#555;font-size:12px;"><strong>Kart Tipi:</strong> ${ekstreKartTipi}</p>
                <p style="margin:2px 0;color:#555;font-size:12px;"><strong>${t('label.cari')}:</strong> ${selectedCari ? selectedCari.adiSoyadi : 'Tümü'}</p>
                <p style="margin:2px 0;color:#555;font-size:12px;"><strong>Tarih Aralığı:</strong> ${new Date(ekstreBaslangic).toLocaleDateString('tr-TR')} - ${new Date(ekstreBitis).toLocaleDateString('tr-TR')}</p>
            </div>
            <table style="width:100%;border-collapse:collapse;margin-bottom:8px;">
                <thead>
                    <tr style="background:#1a2440;color:#fff;">
                        <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">TARİH</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">VADE TARİHİ</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">CARİ</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">AÇIKLAMA</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">ÜRÜN/HİZMET</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:right;font-weight:bold;">BORÇ</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:right;font-weight:bold;">ALACAK</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:right;font-weight:bold;">BAKİYE</th>
                    </tr>
                </thead>
                <tbody>
                    ${rows}
                    <tr style="background:#e67d21;color:#fff;font-weight:bold;">
                        <td colspan="5" style="padding:9px 8px;font-size:11px;text-align:right;border-top:2px solid #c96b15;">TOPLAM</td>
                        <td style="padding:9px 8px;font-size:11px;text-align:right;border-top:2px solid #c96b15;">${formatCurrency(toplamBorc)}</td>
                        <td style="padding:9px 8px;font-size:11px;text-align:right;border-top:2px solid #c96b15;">${formatCurrency(toplamAlacak)}</td>
                        <td style="padding:9px 8px;font-size:11px;text-align:right;border-top:2px solid #c96b15;">${formatCurrency(Math.abs(kalanBakiye))}</td>
                    </tr>
                </tbody>
            </table>
            <p style="color:#aaa;font-size:10px;text-align:right;margin:4px 0 0 0;">YapiCRM &bull; ${new Date().toLocaleDateString('tr-TR')}</p>
        `;

        document.body.appendChild(container);

        try {
            const canvas = await html2canvas(container, { scale: 2, useCORS: true, logging: false });
            const imgData = canvas.toDataURL('image/png');

            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageW = doc.internal.pageSize.getWidth();
            const pageH = doc.internal.pageSize.getHeight();
            const margin = 8;
            const imgW = pageW - 2 * margin;
            const imgH = (canvas.height * imgW) / canvas.width;
            const pageContentH = pageH - 2 * margin;

            let offset = 0;
            let page = 0;
            while (offset < imgH) {
                if (page > 0) doc.addPage();
                doc.addImage(imgData, 'PNG', margin, margin - offset, imgW, imgH);
                offset += pageContentH;
                page++;
            }

            doc.save('cari-hesap-ekstresi.pdf');
            toast.success(t('msg.islemBasarili'));
        } finally {
            document.body.removeChild(container);
        }
    };

    // Excel Export
    const handleEkstreExcelExport = () => {
        const wsData = [
            ['Cari Hesap Ekstresi'],
            [`Tarih: ${new Date(ekstreBaslangic).toLocaleDateString('tr-TR')} - ${new Date(ekstreBitis).toLocaleDateString('tr-TR')}`],
            [],
            ['TARİH', 'VADE TARİHİ', 'CARİ', 'AÇIKLAMA', 'ÜRÜN/HİZMET', 'BORÇ', 'ALACAK', 'BAKİYE'],
            ...ekstreWithBakiye.map(o => [
                new Date(o.islemTarihi).toLocaleDateString('tr-TR'),
                o.vadeTarihi ? new Date(o.vadeTarihi).toLocaleDateString('tr-TR') : '—',
                o.cari?.adiSoyadi || '-',
                `[${o.rowEtiket}] ${o.aciklama || ''}`.trim(),
                o.hizmetUrunAdi || '-',
                o.rowBorc != null ? o.rowBorc : '-',
                o.rowAlacak != null ? o.rowAlacak : '-',
                Math.abs(o.bakiye)
            ]),
            [],
            ['', '', '', 'TOPLAM', toplamBorc, toplamAlacak, Math.abs(kalanBakiye)]
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = [
            { wch: 12 }, { wch: 12 }, { wch: 20 }, { wch: 30 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Cari Ekstre');
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, 'cari-hesap-ekstresi.xlsx');
        toast.success(t('msg.islemBasarili'));
    };

    // === Kasa ve Banka Ekstresi Handlers ===
    const fetchKasalar = async () => {
        try {
            const res = await axiosInstance.get('/api/Kasalar');
            setKasalar(res.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleOpenKasaEkstreModal = () => {
        setShowKasaEkstreModal(true);
        setKasaEkstreReady(false);
        setKasaEkstreOdemeler([]);
        setSelectedKasaID('');
        setKasaEkstrePage(1);
        fetchKasalar();
    };

    const handleKasaEkstreOlustur = async () => {
        setKasaEkstreLoading(true);
        try {
            let data: KasaHareketRecord[] = [];
            if (selectedKasaID) {
                const res = await axiosInstance.get(`/api/KasaHareketleri/kasa/${selectedKasaID}`);
                data = res.data;
            } else {
                // Tüm kasalar
                const kasalarRes = await axiosInstance.get('/api/Kasalar', { params: { aktifMi: true } });
                const tumKasalar: { kasaID: number }[] = kasalarRes.data;
                const results = await Promise.all(
                    tumKasalar.map((k: { kasaID: number }) => axiosInstance.get(`/api/KasaHareketleri/kasa/${k.kasaID}`))
                );
                data = results.flatMap(r => r.data);
            }
            setKasaEkstreOdemeler(data);
            setKasaEkstreReady(true);
            setKasaEkstrePage(1);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setKasaEkstreLoading(false);
        }
    };

    const handleKasaEkstreTemizle = () => {
        setSelectedKasaID('');
        const s = new Date(); s.setMonth(0, 1);
        const e = new Date(); e.setMonth(11, 31);
        const bi = toLocalDateStr(s); const bt = toLocalDateStr(e);
        setKasaEkstreBaslangic(bi); setKasaEkstreBitis(bt);
        setKasaEkstreReady(false); setKasaEkstreOdemeler([]); setKasaEkstrePage(1);
    };

    const kasaEkstreTotalPages = Math.ceil(kasaEkstreWithBakiye.length / kasaEkstrePerPage);
    const paginatedKasaEkstre = kasaEkstreWithBakiye.slice((kasaEkstrePage - 1) * kasaEkstrePerPage, kasaEkstrePage * kasaEkstrePerPage);

    const handleKasaEkstrePdfExport = async () => {
        const selectedKasa = kasalar.find(k => k.kasaID === parseInt(selectedKasaID));

        const container = document.createElement('div');
        container.style.cssText = 'position:fixed;left:-9999px;top:0;width:1140px;font-family:Arial,sans-serif;background:#fff;padding:24px;box-sizing:border-box;';

        const rows = kasaEkstreWithBakiye.map((o, i) => `
            <tr style="background:${i % 2 === 0 ? '#ffffff' : '#f8fafc'};">
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;">${new Date(o.tarih).toLocaleDateString('tr-TR')}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;">${o.kasa ? `${o.kasa.kasaTuru} - ${o.kasa.kasaAdi}` : '-'}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;">${o.cari?.adiSoyadi || '-'}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;">${o.aciklama || o.kategori?.kategoriAdi || '-'}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;text-align:center;">
                    <span style="display:inline-block;padding:2px 7px;border-radius:4px;font-weight:600;font-size:10px;background:${o.islemTuru === 'Giris' ? '#d1fae5' : '#fee2e2'};color:${o.islemTuru === 'Giris' ? '#065f46' : '#991b1b'};">
                        ${o.islemTuru === 'Giris' ? 'GİRİŞ' : 'ÇIKIŞ'}
                    </span>
                </td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;text-align:right;color:#059669;font-weight:600;">${o.rowGiris != null ? '₺' + formatCurrency(o.rowGiris) : '-'}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;text-align:right;color:#dc2626;font-weight:600;">${o.rowCikis != null ? '₺' + formatCurrency(o.rowCikis) : '-'}</td>
                <td style="padding:6px 8px;border:1px solid #e0e0e0;font-size:11px;text-align:right;font-weight:700;color:${o.bakiye >= 0 ? '#059669' : '#dc2626'};">₺${formatCurrency(Math.abs(o.bakiye))}</td>
            </tr>
        `).join('');

        container.innerHTML = `
            <div style="margin-bottom:20px;border-bottom:3px solid #7c3aed;padding-bottom:12px;">
                <h2 style="color:#1a2440;font-size:22px;margin:0 0 6px 0;font-weight:bold;">Kasa ve Banka Ekstresi</h2>
                <p style="margin:2px 0;color:#555;font-size:12px;"><strong>Kasa/Banka:</strong> ${selectedKasa ? selectedKasa.kasaAdi : 'Tümü'}</p>
                <p style="margin:2px 0;color:#555;font-size:12px;"><strong>Tarih Aralığı:</strong> ${kasaEkstreBaslangic ? kasaEkstreBaslangic.split('-').reverse().join('.') : ''} - ${kasaEkstreBitis ? kasaEkstreBitis.split('-').reverse().join('.') : ''}</p>
            </div>
            <table style="width:100%;border-collapse:collapse;margin-bottom:8px;">
                <thead>
                    <tr style="background:#7c3aed;color:#fff;">
                        <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">TARİH</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">KASA/BANKA</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">CARİ</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:left;font-weight:bold;">AÇIKLAMA</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:center;font-weight:bold;">TÜR</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:right;font-weight:bold;">GİRİŞ (+)</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:right;font-weight:bold;">ÇIKIŞ (-)</th>
                        <th style="padding:9px 8px;font-size:11px;text-align:right;font-weight:bold;">BAKİYE</th>
                    </tr>
                </thead>
                <tbody>
                    ${rows}
                    <tr style="background:#7c3aed;color:#fff;font-weight:bold;">
                        <td colspan="5" style="padding:9px 8px;font-size:11px;text-align:right;border-top:2px solid #5b21b6;">TOPLAM</td>
                        <td style="padding:9px 8px;font-size:11px;text-align:right;border-top:2px solid #5b21b6;">₺${formatCurrency(kasaToplamGiris)}</td>
                        <td style="padding:9px 8px;font-size:11px;text-align:right;border-top:2px solid #5b21b6;">₺${formatCurrency(kasaToplamCikis)}</td>
                        <td style="padding:9px 8px;font-size:11px;text-align:right;border-top:2px solid #5b21b6;">₺${formatCurrency(Math.abs(kasaNetBakiye))}</td>
                    </tr>
                </tbody>
            </table>
            <p style="color:#aaa;font-size:10px;text-align:right;margin:4px 0 0 0;">YapiCRM &bull; ${new Date().toLocaleDateString('tr-TR')}</p>
        `;

        document.body.appendChild(container);

        try {
            const canvas = await html2canvas(container, { scale: 2, useCORS: true, logging: false });
            const imgData = canvas.toDataURL('image/png');

            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageW = doc.internal.pageSize.getWidth();
            const pageH = doc.internal.pageSize.getHeight();
            const margin = 8;
            const imgW = pageW - 2 * margin;
            const imgH = (canvas.height * imgW) / canvas.width;
            const pageContentH = pageH - 2 * margin;

            let offset = 0;
            let page = 0;
            while (offset < imgH) {
                if (page > 0) doc.addPage();
                doc.addImage(imgData, 'PNG', margin, margin - offset, imgW, imgH);
                offset += pageContentH;
                page++;
            }

            doc.save('kasa-banka-ekstresi.pdf');
            toast.success(t('msg.islemBasarili'));
        } finally {
            document.body.removeChild(container);
        }
    };

    const handleKasaEkstreExcelExport = () => {
        const selectedKasa = kasalar.find(k => k.kasaID === parseInt(selectedKasaID));
        const wsData: (string | number)[][] = [
            ['Kasa ve Banka Ekstresi'],
            [`Kasa/Banka: ${selectedKasa ? selectedKasa.kasaAdi : 'Tümü'}`],
            [`Tarih Aralığı: ${kasaEkstreBaslangic ? kasaEkstreBaslangic.split('-').reverse().join('.') : ''} - ${kasaEkstreBitis ? kasaEkstreBitis.split('-').reverse().join('.') : ''}`],
            [],
            ['TARİH', 'KASA/BANKA', 'CARİ', 'AÇIKLAMA', 'TÜR', 'GİRİŞ (+)', 'ÇIKIŞ (-)', 'BAKİYE'],
            ...kasaEkstreWithBakiye.map(o => [
                new Date(o.tarih).toLocaleDateString('tr-TR'),
                o.kasa ? `${o.kasa.kasaTuru} - ${o.kasa.kasaAdi}` : '-',
                o.cari?.adiSoyadi || '-',
                o.aciklama || o.kategori?.kategoriAdi || '-',
                o.islemTuru === 'Giris' ? 'GİRİŞ' : 'ÇIKIŞ',
                o.rowGiris != null ? o.rowGiris : 0,
                o.rowCikis != null ? o.rowCikis : 0,
                Math.abs(o.bakiye)
            ]),
            [],
            ['', '', '', '', 'TOPLAM', kasaToplamGiris, kasaToplamCikis, Math.abs(kasaNetBakiye)]
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = [
            { wch: 12 }, { wch: 25 }, { wch: 20 }, { wch: 35 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Kasa Ekstre');
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, 'kasa-banka-ekstresi.xlsx');
        toast.success(t('msg.islemBasarili'));
    };

    const handleOpenModal = () => {
        setShowModal(true);
        setRaporReady(false);
        setAllOdemeler([]);
        setSozlesmeler([]);
        setSelectedProjeID('');
        setYonFiltre('Tümü');
        setGelirPage(1);
        setGiderPage(1);
        setSozlesmePage(1);
    };

    // Proje Gelir-Gider ve Kâr-Zarar Durum Raporu PDF Export (3 Sayfa)
    const handleProjeRaporPdfExport = async () => {
        const selectedProje = projeler.find(p => p.projeID === parseInt(selectedProjeID));

        const thS = (txt: string, right = false) =>
            `<th style="padding:7px 10px;font-size:10px;text-align:${right ? 'right' : 'left'};background:#1a2440;color:#fff;border:1px solid #243259;font-weight:bold;">${txt}</th>`;
        const tdS = (txt: string, right = false, bold = false, color = '#374151') =>
            `<td style="padding:7px 10px;border:1px solid #e0e0e0;font-size:10px;text-align:${right ? 'right' : 'left'};font-weight:${bold ? 700 : 400};color:${color};">${txt}</td>`;

        const reportHeader = `
            <div style="margin-bottom:12px;border-bottom:3px solid #1a2440;padding-bottom:10px;">
                <h2 style="color:#1a2440;font-size:15px;margin:0 0 3px 0;font-weight:bold;">Proje Gelir-Gider ve Kâr-Zarar Durum Raporu</h2>
                <p style="margin:0;color:#555;font-size:9px;"><strong>Proje:</strong> ${selectedProje ? selectedProje.projeAdi : 'Tüm Projeler'} &nbsp;|&nbsp; <strong>Dönem:</strong> ${modalTarihTumu ? 'Tüm Dönem' : new Date(modalBaslangic).toLocaleDateString('tr-TR') + ' — ' + new Date(modalBitis).toLocaleDateString('tr-TR')} &nbsp;|&nbsp; <strong>Rapor Tarihi:</strong> ${new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>
            </div>
        `;

        // SAYFA 1: Özet Kartlar
        const ozetHtml = `
            ${reportHeader}
            <h3 style="color:#0f172a;font-size:10px;margin:0 0 10px 0;font-weight:bold;text-transform:uppercase;letter-spacing:0.06em;">ÖZET</h3>
            <div style="display:flex;gap:12px;margin-bottom:10px;">

                <!-- KART 1: Gelir Özeti -->
                <div style="flex:1;border:1px solid #bbf7d0;border-radius:10px;padding:14px;background:#fff;">
                    <div style="font-size:8px;font-weight:700;color:#15803d;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:10px;border-bottom:2px solid #bbf7d0;padding-bottom:6px;">● Gelir Özeti</div>
                    <div style="font-size:9px;color:#6b7280;margin-bottom:2px;">Toplam Gelir</div>
                    <div style="font-size:17px;font-weight:800;color:#111827;margin-bottom:10px;">₺${formatCurrency(toplamGelir)}</div>
                    <div style="display:flex;justify-content:space-between;margin-bottom:5px;">
                        <span style="font-size:9px;color:#6b7280;">Gelir İşlemleri</span>
                        <span style="font-size:9px;font-weight:700;color:#374151;">${gelirler.length} kayıt</span>
                    </div>
                    <div style="background:#f0fdf4;border:1px solid #dcfce7;border-radius:7px;padding:8px 10px;margin-top:8px;">
                        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
                            <span style="font-size:8px;font-weight:700;color:#15803d;">Gerçekleşen Tahsilat</span>
                            <span style="font-size:8px;font-weight:700;background:#dcfce7;color:#15803d;padding:1px 6px;border-radius:10px;border:1px solid #86efac;">%${tahsilatOrani.toFixed(1)}</span>
                        </div>
                        <div style="height:5px;background:#e2e8f0;border-radius:99px;overflow:hidden;margin-bottom:4px;">
                            <div style="height:5px;width:${Math.min(tahsilatOrani, 100)}%;background:#22c55e;border-radius:99px;"></div>
                        </div>
                        <div style="display:flex;justify-content:space-between;">
                            <span style="font-size:8px;color:#16a34a;">₺${formatCurrency(tahsilEdilen)} tahsil</span>
                            <span style="font-size:8px;color:#d97706;">₺${formatCurrency(bekleyenGelir)} bekleyen</span>
                        </div>
                    </div>
                </div>

                <!-- KART 2: Gider Özeti -->
                <div style="flex:1;border:1px solid #fecdd3;border-radius:10px;padding:14px;background:#fff;">
                    <div style="font-size:8px;font-weight:700;color:#b91c1c;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:10px;border-bottom:2px solid #fecdd3;padding-bottom:6px;">● Gider Özeti</div>
                    <div style="font-size:9px;color:#6b7280;margin-bottom:2px;">Toplam Giderler</div>
                    <div style="font-size:17px;font-weight:800;color:#b91c1c;margin-bottom:10px;">₺${formatCurrency(toplamGider)}</div>
                    ${giderProjeArr.map(([projeAdi, tutar]) => `
                    <div style="display:flex;justify-content:space-between;margin-bottom:4px;">
                        <span style="font-size:9px;color:#6b7280;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:60%;">• ${projeAdi}</span>
                        <span style="font-size:9px;color:#374151;">₺${formatCurrency(tutar)}</span>
                    </div>`).join('')}
                    <div style="border-top:1px solid #f1f5f9;padding-top:8px;margin-top:8px;">
                        <div style="display:flex;justify-content:space-between;margin-bottom:5px;">
                            <span style="font-size:9px;color:#6b7280;">Ödenen Giderler</span>
                            <span style="font-size:9px;font-weight:600;color:#374151;">₺${formatCurrency(giderOdenen)}</span>
                        </div>
                        <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:7px;padding:6px 10px;display:flex;justify-content:space-between;align-items:center;">
                            <span style="font-size:8px;color:#78350f;font-weight:600;">Kalan Borç</span>
                            <span style="font-size:10px;font-weight:700;color:#a16207;">₺${formatCurrency(giderKalanBorc)}</span>
                        </div>
                    </div>
                </div>

                <!-- KART 3: Proje Sağlığı -->
                <div style="flex:1;border:1px solid #e2e8f0;border-radius:10px;padding:14px;background:#fff;">
                    <div style="font-size:8px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:10px;border-bottom:2px solid #e2e8f0;padding-bottom:6px;">● Proje Sağlığı</div>
                    <div style="font-size:9px;color:#6b7280;margin-bottom:4px;">${projeKar >= 0 ? 'Net Dönem Kârı' : 'Bütçe Açığı (Zarar Durumu)'}</div>
                    ${projeKar < 0
                        ? `<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:10px;margin-bottom:10px;">
                            <div style="font-size:8px;font-weight:700;color:#b91c1c;margin-bottom:3px;">⚠ Zarar</div>
                            <div style="font-size:15px;font-weight:900;color:#b91c1c;">₺${formatCurrency(projeKar)}</div>
                           </div>`
                        : `<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:10px;margin-bottom:10px;">
                            <div style="font-size:15px;font-weight:900;color:#15803d;">₺${formatCurrency(projeKar)}</div>
                           </div>`
                    }
                    <div style="font-size:9px;color:#6b7280;margin-bottom:4px;">Net Nakit Pozisyonu</div>
                    <div style="font-size:8px;color:#9ca3af;margin-bottom:5px;">Tahsilat − Ödenen Gider</div>
                    ${netNakitPozisyonu < 0
                        ? `<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:9px;">
                            <div style="font-size:8px;font-weight:700;color:#b91c1c;margin-bottom:2px;">⚠ Negatif nakit</div>
                            <div style="font-size:13px;font-weight:800;color:#b91c1c;">₺${formatCurrency(netNakitPozisyonu)}</div>
                           </div>`
                        : `<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:9px;">
                            <div style="font-size:13px;font-weight:800;color:#15803d;">₺${formatCurrency(netNakitPozisyonu)}</div>
                           </div>`
                    }
                </div>

            </div>
            <p style="color:#aaa;font-size:9px;text-align:right;margin:4px 0 0 0;">YapiCRM &bull; Sayfa 1/3 &bull; ${new Date().toLocaleDateString('tr-TR')}</p>
        `;

        // SAYFA 2: Gelir Listesi (UI ile aynı: tüm GEL-* kayıtlar)
        const gelirPdfRows = gelirler.map((o, i) => {
            const durumText = o.odemeDurumu === 'Odendi' ? 'Tahsil Edildi' : o.odemeDurumu === 'Iptal' ? 'İptal' : (o.vadeTarihi && new Date(o.vadeTarihi) <= bugun) ? 'Gecikmiş' : 'Bekliyor';
            const durumColor = o.odemeDurumu === 'Odendi' ? '#15803d' : o.odemeDurumu === 'Iptal' ? '#64748b' : (o.vadeTarihi && new Date(o.vadeTarihi) <= bugun) ? '#b91c1c' : '#92400e';
            return `<tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'};">
                ${tdS(String(i + 1), true)}${tdS(new Date(o.islemTarihi).toLocaleDateString('tr-TR'))}${tdS(o.ilgiliProje?.projeAdi || o.sozlesme?.projeAdi || '-', false, true)}${tdS(o.sozlesme?.daireNo || '-')}${tdS(o.cari?.adiSoyadi || '-')}${tdS(o.aciklama || '-')}
                ${tdS('₺' + formatCurrency(o.miktar), true, true, '#15803d')}${tdS(durumText, false, true, durumColor)}</tr>`;
        }).join('');

        const sozlesmeListeHtml = `
            ${reportHeader}
            <h3 style="color:#14532d;font-size:10px;margin:0 0 6px 0;font-weight:bold;text-transform:uppercase;letter-spacing:0.06em;border-left:4px solid #15803d;padding-left:8px;">GELİR LİSTESİ (${gelirler.length} kayıt)</h3>
            <table style="width:100%;border-collapse:collapse;margin-bottom:8px;">
                <thead><tr>${thS('#', true)}${thS('TARİH')}${thS('PROJE')}${thS('DAİRE')}${thS('MÜŞTERİ/CARİ')}${thS('AÇIKLAMA')}${thS('TUTAR', true)}${thS('DURUM')}</tr></thead>
                <tbody>${gelirPdfRows}</tbody>
                <tfoot><tr style="background:#dcfce7;">
                    <td colspan="6" style="padding:7px 10px;font-size:10px;font-weight:700;color:#14532d;text-align:right;border:1px solid #86efac;border-top:2px solid #86efac;">TOPLAM</td>
                    <td style="padding:7px 10px;font-size:11px;font-weight:800;color:#15803d;text-align:right;border:1px solid #86efac;border-top:2px solid #86efac;">₺${formatCurrency(toplamGelir)}</td>
                    <td style="border:1px solid #86efac;border-top:2px solid #86efac;"></td>
                </tr></tfoot>
            </table>
            <p style="color:#aaa;font-size:9px;text-align:right;margin:4px 0 0 0;">YapiCRM &bull; Sayfa 2/3 &bull; ${new Date().toLocaleDateString('tr-TR')}</p>
        `;

        // SAYFA 3: Gider Listesi
        const giderRows = giderler.map((o, i) => {
            const odemeDurum = o.odemeDurumu === 'Odendi' ? 'Ödendi' : o.odemeDurumu === 'Iptal' ? 'İptal' : 'Bekliyor';
            const odemeDurumColor = o.odemeDurumu === 'Odendi' ? '#15803d' : o.odemeDurumu === 'Iptal' ? '#64748b' : '#a16207';
            return `<tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'};">
                ${tdS(new Date(o.islemTarihi).toLocaleDateString('tr-TR'))}${tdS(o.ilgiliProje?.projeAdi || o.sozlesme?.projeAdi || '-', false, true)}
                ${tdS((o.hizmetUrunAdi || 'Diğer').toUpperCase())}${tdS(o.aciklama || '-')}${tdS(o.cari?.adiSoyadi || '-')}
                ${tdS('₺' + formatCurrency(o.miktar), true, true, '#b91c1c')}${tdS(odemeDurum, false, true, odemeDurumColor)}</tr>`;
        }).join('');

        const giderListeHtml = `
            ${reportHeader}
            <h3 style="color:#7f1d1d;font-size:10px;margin:0 0 6px 0;font-weight:bold;text-transform:uppercase;letter-spacing:0.06em;border-left:4px solid #b91c1c;padding-left:8px;">GİDER LİSTESİ (${giderler.length} kayıt)</h3>
            <table style="width:100%;border-collapse:collapse;margin-bottom:8px;">
                <thead><tr>${thS('TARİH')}${thS('PROJE')}${thS('KATEGORİ')}${thS('AÇIKLAMA')}${thS('TEDARİKÇİ')}${thS('TUTAR', true)}${thS('DURUM')}</tr></thead>
                <tbody>${giderRows}</tbody>
                <tfoot><tr style="background:#fee2e2;">
                    <td colspan="5" style="padding:7px 10px;font-size:10px;font-weight:700;color:#7f1d1d;text-align:right;border:1px solid #fca5a5;border-top:2px solid #fca5a5;">TOPLAM GİDER</td>
                    <td style="padding:7px 10px;font-size:11px;font-weight:800;color:#b91c1c;text-align:right;border:1px solid #fca5a5;border-top:2px solid #fca5a5;">₺${formatCurrency(toplamGider)}</td>
                    <td style="border:1px solid #fca5a5;border-top:2px solid #fca5a5;"></td>
                </tr></tfoot>
            </table>
            <p style="color:#aaa;font-size:9px;text-align:right;margin:4px 0 0 0;">YapiCRM &bull; Sayfa 3/3 &bull; ${new Date().toLocaleDateString('tr-TR')}</p>
        `;

        const mkContainer = (html: string) => {
            const el = document.createElement('div');
            el.style.cssText = 'position:fixed;left:-9999px;top:0;width:980px;font-family:Arial,sans-serif;background:#fff;padding:24px;box-sizing:border-box;';
            el.innerHTML = html;
            return el;
        };

        const c1 = mkContainer(ozetHtml);
        const c2 = mkContainer(sozlesmeListeHtml);
        const c3 = mkContainer(giderListeHtml);
        document.body.appendChild(c1);
        document.body.appendChild(c2);
        document.body.appendChild(c3);

        try {
            const [canvas1, canvas2, canvas3] = await Promise.all([
                html2canvas(c1, { scale: 2, useCORS: true, logging: false }),
                html2canvas(c2, { scale: 2, useCORS: true, logging: false }),
                html2canvas(c3, { scale: 2, useCORS: true, logging: false }),
            ]);

            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageW = doc.internal.pageSize.getWidth();
            const pageH = doc.internal.pageSize.getHeight();
            const margin = 10;
            const imgW = pageW - 2 * margin;
            const pageContentH = pageH - 2 * margin;

            const addCanvasToDoc = (canvas: HTMLCanvasElement, isFirst: boolean) => {
                if (!isFirst) doc.addPage();
                const imgData = canvas.toDataURL('image/png');
                const imgH = (canvas.height * imgW) / canvas.width;
                let offset = 0;
                let p = 0;
                while (offset < imgH) {
                    if (p > 0) doc.addPage();
                    doc.addImage(imgData, 'PNG', margin, margin - offset, imgW, imgH);
                    offset += pageContentH;
                    p++;
                }
            };

            addCanvasToDoc(canvas1, true);
            addCanvasToDoc(canvas2, false);
            addCanvasToDoc(canvas3, false);

            doc.save('proje-gelir-gider-kar-zarar-raporu.pdf');
            toast.success(t('msg.islemBasarili'));
        } finally {
            document.body.removeChild(c1);
            document.body.removeChild(c2);
            document.body.removeChild(c3);
        }
    };

    const handleRaporOlustur = async () => {

        setModalLoading(true);
        try {
            const [odemeRes, sozlesmeRes] = await Promise.all([
                axiosInstance.get('/api/Odemeler', { params: { aktifMi: true } }),
                axiosInstance.get('/api/Sozlesmeler', { params: { aktifMi: true } }),
            ]);
            setAllOdemeler(odemeRes.data);
            setSozlesmeler(sozlesmeRes.data);
            setRaporReady(true);
            setGelirPage(1);
            setGiderPage(1);
            setSozlesmePage(1);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setModalLoading(false);
        }
    };

    const handleTemizle = () => {
        setSelectedProjeID('');
        const d = new Date();
        d.setMonth(0, 1);
        const e = new Date();
        e.setMonth(11, 31);
        setModalBaslangic(toLocalDateStr(d));
        setModalBitis(toLocalDateStr(e));
        setModalBaslangicDisplay(toLocalDateStr(d).split('-').reverse().join('.'));
        setModalBitisDisplay(toLocalDateStr(e).split('-').reverse().join('.'));
        setModalTarihTumu(false);
        setYonFiltre('Tümü');
        setRaporReady(false);
        setAllOdemeler([]);
        setSozlesmeler([]);
        setGelirPage(1);
        setGiderPage(1);
        setSozlesmePage(1);
    };

    // Proje ile eşleşen ödemeleri filtrele
    const filteredOdemeler = allOdemeler.filter(o => {
        // Proje filtresi
        if (selectedProjeID) {
            const pid = parseInt(selectedProjeID);
            const directMatch = o.projeID === pid || o.ilgiliProje?.projeID === pid;
            const contractMatch = o.sozlesme?.projeID === pid;
            if (!directMatch && !contractMatch) return false;
        }

        // Tarih filtresi
        if (!modalTarihTumu) {
            const tarih = new Date(o.islemTarihi);
            const baslangic = new Date(modalBaslangic);
            const bitis = new Date(modalBitis);
            baslangic.setHours(0, 0, 0, 0);
            bitis.setHours(23, 59, 59, 999);
            if (tarih < baslangic || tarih > bitis) return false;
        }

        // Yön filtresi
        if (yonFiltre === 'Gelir' && o.odemeYonu !== 'Tahsilat') return false;
        if (yonFiltre === 'Gider' && o.odemeYonu !== 'Odeme') return false;

        return true;
    });

    // Gelir & Gider kayıtları
    // İşlemler sayfasıyla aynı mantık: islemTakipNo prefix belirleyicidir
    // GEL-* = Gelir işlemi, GID-* = Gider işlemi, diğerleri = Tahsilat/Ödeme (kasa hareketi)
    const gelirler = filteredOdemeler.filter(o =>
        o.odemeYonu === 'Tahsilat' && o.islemTakipNo.startsWith('GEL-')
    );
    const giderler = filteredOdemeler.filter(o =>
        o.odemeYonu === 'Odeme' && o.islemTakipNo.startsWith('GID-')
    );

    // Gelir istatistikleri
    const toplamGelir = gelirler.reduce((s, o) => s + o.miktar, 0);

    // Tahsilat = İşlemler'deki TAH-* (kasa tahsilat kayıtları) + barter GEL-SOZ-* (taşeron/arsa sahibi daire karşılığı)
    // Not: taksit kayıtları (aciklama "N. taksit") İşlemler sayfasıyla aynı şekilde hariç tutulur
    const tahsilatKayitlari = filteredOdemeler.filter(o =>
        o.odemeYonu === 'Tahsilat' &&
        !o.islemTakipNo.startsWith('GEL-') &&
        !(o.aciklama && /^\d+\.\s*taksit/i.test(o.aciklama))
    );
    const barterGelirler = gelirler.filter(o =>
        o.aciklama?.includes('Barter') || o.aciklama?.includes('Mahsup')
    );
    const tahsilEdilen =
        tahsilatKayitlari.reduce((s, o) => s + o.miktar, 0) +
        barterGelirler.reduce((s, o) => s + o.miktar, 0);

    const bugun = new Date();
    bugun.setHours(23, 59, 59, 999);
    const bekleyenGelir = gelirler.filter(o => o.odemeDurumu === 'Bekliyor' && (!o.vadeTarihi || new Date(o.vadeTarihi) > bugun)).reduce((s, o) => s + o.miktar, 0);
    const gecikenGelir = gelirler.filter(o => o.odemeDurumu === 'Bekliyor' && o.vadeTarihi && new Date(o.vadeTarihi) <= bugun).reduce((s, o) => s + o.miktar, 0);

    // Gider istatistikleri
    const toplamGider = giderler.reduce((s, o) => s + o.miktar, 0);

    // Proje bazında gider dağılımı (bar chart)
    const projeGiderMap = new Map<string, number>();
    giderler.forEach(o => {
        const projeAdi = o.ilgiliProje?.projeAdi || o.sozlesme?.projeAdi || 'Diğer';
        projeGiderMap.set(projeAdi, (projeGiderMap.get(projeAdi) || 0) + o.miktar);
    });
    const projeGiderArr = Array.from(projeGiderMap.entries()).sort((a, b) => b[1] - a[1]);

    // Kategori dağılımı (doughnut chart) - hizmetUrunAdi bazında
    const kategoriMap = new Map<string, number>();
    giderler.forEach(o => {
        const kat = o.hizmetUrunAdi || 'Diğer';
        kategoriMap.set(kat, (kategoriMap.get(kat) || 0) + o.miktar);
    });
    const kategoriArr = Array.from(kategoriMap.entries()).sort((a, b) => b[1] - a[1]);

    const barData = {
        labels: projeGiderArr.map(p => p[0]),
        datasets: [{
            label: 'Gider',
            data: projeGiderArr.map(p => p[1]),
            backgroundColor: 'rgba(244, 63, 94, 0.65)',
            borderColor: 'rgba(244, 63, 94, 0.9)',
            borderWidth: 0,
            borderRadius: 6,
        }]
    };

    const doughnutColors = ['#ff6b6b', '#feca57', '#48dbfb', '#a29bfe', '#ff9ff3', '#54a0ff', '#5f27cd', '#01a3a4'];
    const doughnutData = {
        labels: kategoriArr.map(k => k[0]),
        datasets: [{
            data: kategoriArr.map(k => k[1]),
            backgroundColor: doughnutColors.slice(0, kategoriArr.length),
            borderWidth: 2,
            borderColor: '#fff',
        }]
    };

    // Tablo filtre
    const showGelir = yonFiltre === 'Tümü' || yonFiltre === 'Gelir';
    const showGider = yonFiltre === 'Tümü' || yonFiltre === 'Gider';

    // Sayfalama
    const gelirTotalPages = Math.ceil(gelirler.length / perPage);
    const paginatedGelirler = gelirler.slice((gelirPage - 1) * perPage, gelirPage * perPage);
    const giderTotalPages = Math.ceil(giderler.length / perPage);
    const paginatedGiderler = giderler.slice((giderPage - 1) * perPage, giderPage * perPage);

    // ===== SÖZLEŞME BAZLI HESAPLAR =====
    const filteredSozlesmeler = sozlesmeler.filter(s => {
        if (selectedProjeID) {
            const pid = parseInt(selectedProjeID);
            const projeID = s.daire?.blok?.proje?.projeID;
            if (projeID !== pid) return false;
        }
        return s.sozlesmeDurumu === 'Sözleşme';
    });
    const toplamSozlesmeTutari = filteredSozlesmeler.reduce((sum, s) => sum + s.sozlesmeFiyati, 0);

    // Proje bazında sözleşme dağılımı
    const sozlesmeProjeMap = new Map<string, number>();
    filteredSozlesmeler.forEach(s => {
        const projeAdi = s.daire?.blok?.proje?.projeAdi || 'Diğer';
        sozlesmeProjeMap.set(projeAdi, (sozlesmeProjeMap.get(projeAdi) || 0) + s.sozlesmeFiyati);
    });
    const sozlesmeProjeArr = Array.from(sozlesmeProjeMap.entries()).sort((a, b) => b[1] - a[1]);

    const tahsilatOrani = toplamGelir > 0 ? (tahsilEdilen / toplamGelir * 100) : 0;

    // Her sözleşme için tahsil edilen miktar (gelirlerden sozlesmeID eşleştirme)
    const sozlesmeTahsilatMap = new Map<number, number>();
    gelirler.filter(o => o.odemeDurumu === 'Odendi' && o.sozlesme?.sozlesmeID).forEach(o => {
        const sid = o.sozlesme!.sozlesmeID;
        sozlesmeTahsilatMap.set(sid, (sozlesmeTahsilatMap.get(sid) || 0) + o.miktar);
    });

    // Gider alt hesaplar
    // giderOdenen = İşlemler'deki Ödeme sekmesi (ODE-* prefix, taksit hariç) — tahsilat mantığıyla simetrik
    const odemeKayitlari = filteredOdemeler.filter(o =>
        o.odemeYonu === 'Odeme' &&
        !o.islemTakipNo.startsWith('GID-') &&
        !(o.aciklama && /^\d+\.\s*taksit/i.test(o.aciklama))
    );
    const giderOdenen = odemeKayitlari.reduce((s, o) => s + o.miktar, 0);
    const giderKalanBorc = toplamGider - giderOdenen;

    // Proje bazında gider dağılımı
    const giderProjeMap = new Map<string, number>();
    giderler.forEach(o => {
        const projeAdi = o.ilgiliProje?.projeAdi || o.sozlesme?.projeAdi || 'Diğer';
        giderProjeMap.set(projeAdi, (giderProjeMap.get(projeAdi) || 0) + o.miktar);
    });
    const giderProjeArr = Array.from(giderProjeMap.entries()).sort((a, b) => b[1] - a[1]);

    // Sözleşme dışı gelirler (finans modülünde sözleşmeye bağlı olmayan tahsilatlar)
    const digerGelirler = gelirler.filter(o => !o.sozlesmeOdemesi);
    const toplamDigerGelir = digerGelirler.reduce((s, o) => s + o.miktar, 0);
    // Kategori bazında diğer gelir dağılımı
    const digerGelirKategoriMap = new Map<string, number>();
    digerGelirler.forEach(o => {
        const kat = o.hizmetUrunAdi || o.odemeTuru || 'Diğer Gelir';
        digerGelirKategoriMap.set(kat, (digerGelirKategoriMap.get(kat) || 0) + o.miktar);
    });
    const digerGelirKategoriArr = Array.from(digerGelirKategoriMap.entries()).sort((a, b) => b[1] - a[1]);

    // Genel toplam gelir = tüm GEL- işlemleri (barter ve sözleşme dahil)
    const toplamGenelGelir = toplamGelir;

    // Kâr-zarar
    const projeKar = toplamGelir - toplamGider;
    const netNakitPozisyonu = tahsilEdilen - giderOdenen;

    // Sözleşme sayfalaması
    const sozlesmeTotalPages = Math.ceil(filteredSozlesmeler.length / perPage);
    const paginatedSozlesmeler = filteredSozlesmeler.slice((sozlesmePage - 1) * perPage, sozlesmePage * perPage);

    const formatCurrency = (val: number) => val.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const getDurumBadge = (o: OdemeRecord) => {
        if (o.odemeDurumu === 'Odendi') return { text: 'Ödendi', bg: '#dcfce7', color: '#15803d' };
        if (o.odemeDurumu === 'Iptal') return { text: 'İptal', bg: '#f1f5f9', color: '#64748b' };
        if (o.vadeTarihi && new Date(o.vadeTarihi) <= bugun) return { text: 'Gecikmiş', bg: '#fee2e2', color: '#b91c1c' };
        return { text: 'Bekliyor', bg: '#fef9c3', color: '#a16207' };
    };

    const getKategoriBadge = (kat: string | null) => {
        const k = (kat || 'Diğer').toUpperCase();
        const colors: Record<string, { bg: string; color: string }> = {
            'MALZEME': { bg: '#ff6b6b', color: '#fff' },
            'İŞÇİLİK': { bg: '#feca57', color: '#333' },
            'MAKİNA': { bg: '#48dbfb', color: '#333' },
            'MAKINA': { bg: '#48dbfb', color: '#333' },
        };
        return colors[k] || { bg: '#a29bfe', color: '#fff' };
    };

    const cardStyle: React.CSSProperties = {
        background: '#fff', borderRadius: '16px', padding: '24px',
        border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
    };

    const fmt = (n: number) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n) + ' ₺';

    const raporListesi: RaporItemDef[] = [
        {
            id: 'proje-gelir-gider',
            title: t('rapor.projeGelirGider.title', 'Proje Gelir Gider Raporu'),
            desc: t('rapor.projeGelirGider.desc', 'Proje bazında gelir ve gider analizini görüntüleyin'),
            category: 'Finansal',
            iconBg: '#fff7ed', iconBorder: '#fed7aa', accentColor: '#cd6906', accentShadow: 'rgba(205,105,6,0.15)',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#cd6906" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
            onClick: handleOpenModal,
        },
        {
            id: 'cari-ekstre',
            title: t('rapor.cariEkstre.title', 'Cari Hesap Ekstresi'),
            desc: t('rapor.cariEkstre.desc', 'Müşteri ve tedarikçi cari hesap hareketlerini görüntüleyin'),
            category: 'Finansal',
            iconBg: '#eff6ff', iconBorder: '#bfdbfe', accentColor: '#2563eb', accentShadow: 'rgba(37,99,235,0.15)',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
            onClick: handleOpenEkstreModal,
        },
        {
            id: 'kasa-banka-ekstre',
            title: t('rapor.kasaEkstre.title', 'Kasa ve Banka Ekstresi'),
            desc: t('rapor.kasaEkstre.desc', 'Kasa ve banka hesaplarının giriş-çıkış hareketlerini görüntüleyin'),
            category: 'Finansal',
            iconBg: '#f5f3ff', iconBorder: '#ddd6fe', accentColor: '#7c3aed', accentShadow: 'rgba(124,58,237,0.15)',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/></svg>,
            onClick: handleOpenKasaEkstreModal,
        },
        {
            id: 'nakit-akis-proj',
            title: t('rapor.nakitAkis.title', 'Nakit Akış ve Projeksiyon Raporu'),
            desc: t('rapor.nakitAkis.desc', 'Vade bazlı nakit giriş/çıkış projeksiyonu — A4 yazdırılabilir detaylı rapor'),
            category: 'Performans',
            isNew: true,
            newBadgeStyle: { background: '#dbeafe', color: '#1d4ed8' },
            iconBg: '#f0f9ff', iconBorder: '#bae6fd', accentColor: '#0284c7', accentShadow: 'rgba(2,132,199,0.15)',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 12h8M8 8h8M8 16h4"/></svg>,
            onClick: () => navigate('/nakit-akis-proj'),
        },
        {
            id: 'envanter-raporu',
            title: t('rapor.envanter.title', 'Kalan Envanter ve Stok Değer Raporu'),
            desc: t('rapor.envanter.desc', 'Satılmamış bağımsız bölümler, stok değeri ve satış oranı — A4 yazdırılabilir'),
            category: 'Stok',
            isNew: true,
            newBadgeStyle: { background: '#f3e8ff', color: '#7c3aed' },
            iconBg: '#faf5ff', iconBorder: '#e9d5ff', accentColor: '#7c3aed', accentShadow: 'rgba(124,58,237,0.15)',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
            onClick: () => navigate('/envanter-raporu'),
        },
        {
            id: 'tahsilat-performans',
            title: t('rapor.tahsilat.title', 'Tahsilat Performansı ve Gerçekleşme Raporu'),
            desc: t('rapor.tahsilat.desc', 'Vadesi gelen tahsilat, kasa girişi, gecikmiş alacak ve başarı oranı — A4 yazdırılabilir'),
            category: 'Performans',
            isNew: true,
            newBadgeStyle: { background: '#dcfce7', color: '#15803d' },
            iconBg: '#f0fdf4', iconBorder: '#bbf7d0', accentColor: '#16a34a', accentShadow: 'rgba(22,163,74,0.15)',
            icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
            onClick: () => navigate('/tahsilat-performans'),
        },
    ];

    const filteredRaporlar = raporListesi.filter(r => {
        const categoryMatch = activeCategory === 'Tümü' || r.category === activeCategory;
        const searchMatch = !searchQuery || r.title.toLowerCase().includes(searchQuery.toLowerCase()) || r.desc.toLowerCase().includes(searchQuery.toLowerCase());
        return categoryMatch && searchMatch;
    });

    const favoriRaporlarItems = raporListesi.filter(r => favoriRaporlar.includes(r.id));

    return (
        <div style={{ padding: '24px', fontFamily: "'Inter', 'Roboto', sans-serif", color: '#1f2937' }}>
            <style>{`
                @media print {
                    @page { size: A4 portrait; margin: 15mm; }
                    body { background: #fff !important; }
                    .main-header, .main-sidebar, .main-footer, .content-header, nav, aside { display: none !important; width: 0 !important; height: 0 !important; }
                    .content-wrapper { margin-left: 0 !important; padding: 0 !important; background: #fff !important; }
                    body.sidebar-mini .content-wrapper, body.layout-fixed .content-wrapper { margin-left: 0 !important; }
                    .raporlar-modal-backdrop { background: transparent !important; position: static !important; padding: 0 !important; }
                    .raporlar-modal-close-btn { display: none !important; }
                    .raporlar-modal-sticky-header { position: static !important; }
                    .raporlar-modal-box { border-radius: 0 !important; max-height: none !important; overflow: visible !important; box-shadow: none !important; }
                    .raporlar-filter-section { display: none !important; }
                    .rapor-sayfa-gecisi { page-break-before: always !important; }
                    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                }
                .rapor-kartlar-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
                @media (max-width: 600px) {
                    .rapor-kartlar-grid { grid-template-columns: 1fr; gap: 12px; }
                }
                .hub-metric-card { transition: box-shadow 0.2s, transform 0.2s; }
                .hub-metric-card:hover { transform: translateY(-2px); }
                .hub-category-pill { cursor: pointer; padding: 6px 18px; border-radius: 999px; font-size: 0.82rem; font-weight: 600; border: 1.5px solid transparent; transition: all 0.15s; }
                .hub-category-pill:hover { opacity: 0.85; }
                .rapor-hub-card { display: flex; align-items: center; gap: 20px; cursor: pointer; transition: all 0.2s ease; position: relative; }
                .rapor-hub-card:hover { transform: translateY(-2px); }
                .rapor-star-btn { background: none; border: none; cursor: pointer; padding: 4px; border-radius: 6px; transition: color 0.15s, background 0.15s; color: #cbd5e1; flex-shrink: 0; }
                .rapor-star-btn:hover { color: #f59e0b; background: #fef9c3; }
                .rapor-star-btn.active { color: #f59e0b; }
            `}</style>

            {/* === REPORTING HUB BAŞLIK === */}
            <div style={{ marginBottom: '28px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', margin: '0 0 4px 0', letterSpacing: '-0.025em' }}>
                    {t('menu.raporlar', 'Raporlar')}
                </h1>
                <p style={{ fontSize: '13.5px', color: '#6b7280', margin: 0 }}>
                    {t('raporlar.aciklama', 'Gelir, gider, stok ve performans analizlerinizi tek merkezden yönetin.')}
                </p>
            </div>

            {/* === FİLTRE BARI === */}
            <div className="raporlar-filter-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
                {/* Kategori Pilleri */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {(['Tümü', 'Finansal', 'Stok', 'Performans'] as const).map(cat => (
                        <button
                            key={cat}
                            className="hub-category-pill"
                            onClick={() => setActiveCategory(cat)}
                            style={{
                                background: activeCategory === cat ? '#1e293b' : '#f8fafc',
                                color: activeCategory === cat ? '#fff' : '#64748b',
                                borderColor: activeCategory === cat ? '#1e293b' : '#e2e8f0',
                            }}
                        >
                            {cat === 'Tümü' ? t('rapor.kat.tumu', 'Tümü') : cat === 'Finansal' ? t('rapor.kat.finansal', 'Finansal Raporlar') : cat === 'Stok' ? t('rapor.kat.stok', 'Stok & Envanter') : t('rapor.kat.performans', 'Performans Analizleri')}
                        </button>
                    ))}
                </div>
                {/* Arama */}
                <div style={{ position: 'relative' }}>
                    <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input
                        type="text"
                        placeholder={t('raporlar.raporAra', 'Rapor ara…')}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        style={{ paddingLeft: '32px', paddingRight: '12px', paddingTop: '7px', paddingBottom: '7px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '0.83rem', color: '#374151', outline: 'none', background: '#fff', width: '200px' }}
                    />
                </div>
            </div>

            {/* === FAVORI RAPORLAR === */}
            {favoriRaporlarItems.length > 0 && (
                <div style={{ marginBottom: '28px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('raporlar.sikKullanilanRaporlar', 'Sık Kullanılan Raporlar')}</span>
                    </div>
                    <div className="rapor-kartlar-grid">
                        {favoriRaporlarItems.map(r => (
                            <RaporKartItem key={'fav-' + r.id} r={r} isFavori={true} onToggleFavori={toggleFavori} cardStyle={cardStyle} />
                        ))}
                    </div>
                    <hr style={{ margin: '24px 0 0', border: 'none', borderTop: '1px solid #f1f5f9' }} />
                </div>
            )}

            {/* === TÜM RAPORLAR === */}
            {filteredRaporlar.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: '#9ca3af' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <p style={{ margin: 0, fontWeight: 500 }}>{t('raporlar.raporBulunamadi', 'Aramanızla eşleşen rapor bulunamadı.')}</p>
                </div>
            ) : (
                <div className="rapor-kartlar-grid">
                    {filteredRaporlar.map(r => (
                        <RaporKartItem key={r.id} r={r} isFavori={favoriRaporlar.includes(r.id)} onToggleFavori={toggleFavori} cardStyle={cardStyle} />
                    ))}
                </div>
            )}


                        {/* CARİ HESAP EKSTRESİ MODAL */}
            {showEkstreModal && (
                <div className="raporlar-modal-backdrop" style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
                    padding: '20px', overflowY: 'auto'
                }}>
                    <div className="raporlar-modal-box" style={{
                        background: '#f8fafc', borderRadius: '20px', width: '100%', maxWidth: '1200px',
                        maxHeight: '95vh', overflowY: 'auto', position: 'relative'
                    }}>
                        {/* Modal Header */}
                        <div className="raporlar-modal-sticky-header" style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '20px 28px', borderBottom: '1px solid #e2e8f0', background: '#fff',
                            borderRadius: '20px 20px 0 0', position: 'sticky', top: 0, zIndex: 10
                        }}>
                            <h3 style={{ margin: 0, fontWeight: 700, color: '#0f172a', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, background: '#fff7ed', border: '1px solid #fed7aa' }}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#cd6906" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                </span>
                                {t('rapor.cariEkstre.title', 'Cari Hesap Ekstresi')}
                            </h3>
                            <button className="raporlar-modal-close-btn" onClick={() => setShowEkstreModal(false)} style={{
                                background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer',
                                color: '#94a3b8', padding: '4px 8px', borderRadius: '8px'
                            }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        </div>

                        <div style={{ padding: '24px 28px' }}>
                            {/* Filtreler */}
                            <div className="raporlar-filter-section" style={{
                                background: '#fff', borderRadius: '12px', padding: '20px 24px',
                                marginBottom: '24px', border: '1px solid #e5e7eb'
                            }}>
                                <h5 style={{ margin: '0 0 16px', fontWeight: 600, color: '#374151', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 7 }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
                                    {t('rapor.raporFiltreleri', 'Rapor Filtreleri')}
                                </h5>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={ekstreFilterLabelStyle}>{t('rapor.kartTipi', 'Kart Tipi')}</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            {(['Müşteri', 'Tedarikçi'] as const).map(tipi => (
                                                <button
                                                    key={tipi}
                                                    onClick={() => handleEkstreKartTipiChange(tipi)}
                                                    style={{
                                                        flex: 1, padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
                                                        fontSize: '0.8rem', fontWeight: 600, transition: 'all 0.15s',
                                                        border: ekstreKartTipi === tipi ? 'none' : '1px solid #d1d5db',
                                                        background: ekstreKartTipi === tipi ? '#cd6906' : '#fff',
                                                        color: ekstreKartTipi === tipi ? '#fff' : '#6b7280',
                                                    }}
                                                >{t('rapor.kartTipi.' + tipi, tipi)}</button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label style={ekstreFilterLabelStyle}>{t('label.baslangicTarihi', 'Başlangıç Tarihi')}</label>
                                        <div style={{width:'100%'}}>
                                            <TrDateInput
                                                style={ekstreFilterInputStyle}
                                                value={ekstreBaslangic}
                                                onChange={e => setEkstreBaslangic(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label style={ekstreFilterLabelStyle}>{t('label.bitisTarihi', 'Bitiş Tarihi')}</label>
                                        <div style={{width:'100%'}}>
                                            <TrDateInput
                                                style={ekstreFilterInputStyle}
                                                value={ekstreBitis}
                                                onChange={e => setEkstreBitis(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ position: 'relative' }}>
                                        <label style={ekstreFilterLabelStyle}>{t('rapor.kartTipi.' + ekstreKartTipi, ekstreKartTipi) + ' ' + t('rapor.kartSecimi', 'Kart Seçimi')}</label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="text"
                                                placeholder={t('label.ara')}
                                                value={cariSearch || (selectedCariID ? (cariler.find(c => String(c.cariID) === selectedCariID)?.adiSoyadi ?? '') : '')}
                                                onFocus={() => { setCariSearch(''); setShowCariDropdown(true); }}
                                                onChange={e => { setCariSearch(e.target.value); setShowCariDropdown(true); }}
                                                onBlur={() => setTimeout(() => setShowCariDropdown(false), 150)}
                                                style={{ ...ekstreFilterInputStyle, paddingRight: '28px', cursor: 'text' }}
                                            />
                                            <svg style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                        </div>
                                        {showCariDropdown && (
                                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 16px rgba(15,23,42,0.12)', zIndex: 9999, maxHeight: '220px', overflowY: 'auto', marginTop: '2px' }}>
                                                <div
                                                    onMouseDown={() => { setSelectedCariID(''); setCariSearch(''); setShowCariDropdown(false); }}
                                                    style={{ padding: '8px 12px', fontSize: '0.8125rem', color: selectedCariID === '' ? '#cd6906' : '#374151', fontWeight: selectedCariID === '' ? 600 : 400, cursor: 'pointer', background: selectedCariID === '' ? '#fff8f0' : 'transparent' }}
                                                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
                                                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = selectedCariID === '' ? '#fff8f0' : 'transparent'; }}
                                                >{t('label.tumu', 'Tümü')}</div>
                                                {cariler
                                                    .filter(c => !cariSearch || c.adiSoyadi.toLowerCase().includes(cariSearch.toLowerCase()))
                                                    .map(c => (
                                                        <div
                                                            key={c.cariID}
                                                            onMouseDown={() => { setSelectedCariID(String(c.cariID)); setCariSearch(''); setShowCariDropdown(false); }}
                                                            style={{ padding: '8px 12px', fontSize: '0.8125rem', color: String(c.cariID) === selectedCariID ? '#cd6906' : '#374151', fontWeight: String(c.cariID) === selectedCariID ? 600 : 400, cursor: 'pointer', background: String(c.cariID) === selectedCariID ? '#fff8f0' : 'transparent', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
                                                            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = String(c.cariID) === selectedCariID ? '#fff8f0' : 'transparent'; }}
                                                        >{c.adiSoyadi}</div>
                                                    ))
                                                }
                                                {cariler.filter(c => !cariSearch || c.adiSoyadi.toLowerCase().includes(cariSearch.toLowerCase())).length === 0 && (
                                                    <div style={{ padding: '10px 12px', fontSize: '0.8125rem', color: '#9ca3af', textAlign: 'center' }}>{t('msg.sonucBulunamadi', 'Sonuç bulunamadı')}</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <button onClick={handleEkstreTemizle} style={{
                                        padding: '10px 24px', borderRadius: '10px', border: '1px solid #d1d5db',
                                        background: '#fff', color: '#475569', fontWeight: 600, fontSize: '0.85rem',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                                    }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg> {t('btn.temizle', 'Temizle')}
                                    </button>
                                    <button onClick={handleEkstreOlustur} style={{
                                        padding: '9px 20px', borderRadius: '8px', border: 'none',
                                        background: '#cd6906', color: '#fff',
                                        fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 1px 3px rgba(205,105,6,0.3)'
                                    }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> {t('btn.raporOlustur', 'Rapor Oluştur')}
                                    </button>
                                </div>
                            </div>

                            {/* Loading */}
                            {ekstreLoading && (
                                <div style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                    <p style={{ fontWeight: 600 }}>{t('rapor.olusturuluyor', 'Rapor oluşturuluyor...')}</p>
                                </div>
                            )}

                            {/* Rapor İçeriği */}
                            {ekstreReady && !ekstreLoading && (
                                <div>
                                    {/* Başlık */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h5 style={{ margin: 0, fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#cd6906" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                            {t('rapor.cariEkstre.hareketler', 'Cari Hesap Hareketleri')}
                                        </h5>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button onClick={handleEkstrePdfExport} style={ekstrePdfBtnStyle}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> PDF
                                            </button>
                                            <button onClick={handleEkstreExcelExport} style={ekstreExcelBtnStyle}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> Excel
                                            </button>
                                        </div>
                                    </div>

                                    {/* Özet Kartları */}
                                    <div style={{
                                        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px'
                                    }}>
                                        <div style={ekstreSummaryCard}>
                                            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('rapor.toplamBorc', 'Toplam Borç')}</div>
                                            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>₺{formatCurrency(toplamBorc)}</div>
                                        </div>
                                        <div style={ekstreSummaryCard}>
                                            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('rapor.toplamAlacak', 'Toplam Alacak')}</div>
                                            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>₺{formatCurrency(toplamAlacak)}</div>
                                        </div>
                                        <div style={ekstreSummaryCard}>
                                            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('rapor.kalanBakiye', 'Kalan Bakiye')}{kalanBaYonu ? ` (${kalanBaYonu})` : ''}</div>
                                            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: kalanBaYonu === 'B' ? '#ef4444' : kalanBaYonu === 'A' ? '#10b981' : '#6b7280', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>₺{formatCurrency(kalanBakiye)}</div>
                                        </div>
                                    </div>

                                    {/* Tablo */}
                                    {ekstreWithBakiye.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '48px', color: '#94a3b8', background: '#fff', borderRadius: '16px', border: '2px dashed #e2e8f0' }}>
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                            <p style={{ margin: 0, fontWeight: 600, color: '#475569' }}>{t('msg.cariKaydiYok', 'Seçilen kriterlere uygun cari kaydı bulunamadı')}</p>
                                        </div>
                                    ) : (
                                        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
                                            <div style={{ overflowX: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                                    <thead>
                                                        <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                                            <th style={thStyle}>{t('label.tarih')}</th>
                                                            <th style={thStyle}>{t('label.vadeTarihi', 'Vade Tarihi')}</th>
                                                            <th style={thStyle}>{t('label.cari', 'Cari')}</th>
                                                            <th style={thStyle}>{t('label.aciklama')}</th>
                                                            <th style={thStyle}>{t('label.urunHizmet', 'Ürün/Hizmet')}</th>
                                                            <th style={{ ...thStyle, textAlign: 'right' }}>{t('label.borc', 'Borç')}</th>
                                                            <th style={{ ...thStyle, textAlign: 'right' }}>{t('label.alacak', 'Alacak')}</th>
                                                            <th style={{ ...thStyle, textAlign: 'right' }}>{t('label.bakiye', 'Bakiye')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginatedEkstre.map(o => {
                                                            return (
                                                                <tr key={o.rowKey}
                                                                    style={{ transition: 'background 0.15s', background: o.rowEtiket === 'Sözleşme' ? '#fffbeb' : (o.rowEtiket === 'Ödeme Makbuzu' || o.rowEtiket === 'Tahsilat Makbuzu' ? '#f0fdf4' : undefined) }}
                                                                    onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
                                                                    onMouseLeave={e => (e.currentTarget.style.background = o.rowEtiket === 'Sözleşme' ? '#fffbeb' : (o.rowEtiket === 'Ödeme Makbuzu' || o.rowEtiket === 'Tahsilat Makbuzu' ? '#f0fdf4' : 'transparent'))}
                                                                >
                                                                    <td style={tdStyle}>{new Date(o.islemTarihi).toLocaleDateString('tr-TR')}</td>
                                                                    <td style={tdStyle}>{o.vadeTarihi ? new Date(o.vadeTarihi).toLocaleDateString('tr-TR') : '—'}</td>
                                                                    <td style={{ ...tdStyle, fontWeight: 700, color: '#0f172a' }}>{o.cari?.adiSoyadi || '-'}</td>
                                                                    <td style={tdStyle}>
                                                                        <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, marginRight: 6, background: o.rowEtiket === 'Sözleşme' ? '#fef3c7' : (o.rowEtiket === 'Fatura' ? '#e0f2fe' : '#d1fae5'), color: o.rowEtiket === 'Sözleşme' ? '#92400e' : (o.rowEtiket === 'Fatura' ? '#0369a1' : '#065f46'), border: o.rowEtiket === 'Sözleşme' ? '1px solid #fbbf24' : (o.rowEtiket === 'Fatura' ? '1px solid #7dd3fc' : '1px solid #a7f3d0') }}>{o.rowEtiket}</span>
                                                                        {o.aciklama || ''}
                                                                    </td>
                                                                    <td style={tdStyle}>{o.hizmetUrunAdi || '-'}</td>
                                                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#ef4444' }}>
                                                                        {o.rowBorc != null ? `₺${formatCurrency(o.rowBorc)}` : '-'}
                                                                    </td>
                                                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#10b981' }}>
                                                                        {o.rowAlacak != null ? `₺${formatCurrency(o.rowAlacak)}` : '-'}
                                                                    </td>
                                                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: o.baYonu === 'B' ? '#ef4444' : o.baYonu === 'A' ? '#10b981' : '#6b7280' }}>
                                                                        ₺{formatCurrency(o.bakiye)}{o.baYonu && <span style={{ fontSize: '0.65rem', marginLeft: 2, opacity: 0.7 }}>({o.baYonu})</span>}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {filteredEkstre.length > ekstrePerPage && renderPagination(ekstrePage, ekstreTotalPages, setEkstrePage)}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Henüz rapor oluşturulmadı */}
                            {!ekstreReady && !ekstreLoading && (
                                <div style={{
                                    textAlign: 'center', padding: '60px 20px', color: '#94a3b8',
                                    background: '#fff', borderRadius: '16px', border: '2px dashed #e2e8f0'
                                }}>
                                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                    <p style={{ margin: 0, fontWeight: 600, color: '#475569', fontSize: '1rem' }}>
                                        {t('rapor.filtreSecin', 'Filtreleri seçin ve "Rapor Oluştur" butonuna tıklayın')}
                                    </p>
                                    <p style={{ margin: '8px 0 0', color: '#94a3b8', fontSize: '0.85rem' }}>
                                        {t('rapor.cariEkstreIpucu', 'Hesap tipi ve cari kart seçerek ekstre oluşturabilirsiniz')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* KASA VE BANKA EKSTRESİ MODAL */}
            {showKasaEkstreModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
                    padding: '20px', overflowY: 'auto'
                }}>
                    <div style={{
                        background: '#f8fafc', borderRadius: '20px', width: '100%', maxWidth: '1200px',
                        maxHeight: '95vh', overflowY: 'auto', position: 'relative'
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '20px 28px', borderBottom: '1px solid #e2e8f0', background: '#fff',
                            borderRadius: '20px 20px 0 0', position: 'sticky', top: 0, zIndex: 10
                        }}>
                            <h3 style={{ margin: 0, fontWeight: 700, color: '#0f172a', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, background: '#f5f3ff', border: '1px solid #ddd6fe' }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/></svg>
                                </span>
                                {t('rapor.kasaEkstre.title', 'Kasa ve Banka Ekstresi')}
                            </h3>
                            <button onClick={() => setShowKasaEkstreModal(false)} style={{
                                background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer',
                                color: '#94a3b8', padding: '4px 8px', borderRadius: '8px'
                            }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        </div>

                        <div style={{ padding: '24px 28px' }}>
                            {/* Filtreler */}
                            <div style={{
                                background: '#fff', borderRadius: '12px', padding: '20px 24px',
                                marginBottom: '24px', border: '1px solid #e5e7eb'
                            }}>
                                <h5 style={{ margin: '0 0 16px', fontWeight: 600, color: '#374151', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 7 }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
                                    {t('rapor.raporFiltreleri', 'Rapor Filtreleri')}
                                </h5>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                                    {/* Kasa Seçimi */}
                                    <div style={{ position: 'relative' }}>
                                        <label style={ekstreFilterLabelStyle}>{t('rapor.kasaBankaHesabi', 'Kasa / Banka Hesabı')}</label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="text"
                                                placeholder={t('label.ara')}
                                                value={kasaSearch || (selectedKasaID ? (kasalar.find(k => String(k.kasaID) === selectedKasaID)?.kasaAdi ?? '') : '')}
                                                onFocus={() => { setKasaSearch(''); setShowKasaDropdown(true); }}
                                                onChange={e => { setKasaSearch(e.target.value); setShowKasaDropdown(true); }}
                                                onBlur={() => setTimeout(() => setShowKasaDropdown(false), 150)}
                                                style={{ ...ekstreFilterInputStyle, paddingRight: '28px', cursor: 'text' }}
                                            />
                                            <svg style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                        </div>
                                        {showKasaDropdown && (
                                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 16px rgba(15,23,42,0.12)', zIndex: 9999, maxHeight: '320px', overflowY: 'auto', marginTop: '2px' }}>
                                                <div
                                                    onMouseDown={() => { setSelectedKasaID(''); setKasaSearch(''); setShowKasaDropdown(false); }}
                                                    style={{ padding: '8px 12px', fontSize: '0.8125rem', color: selectedKasaID === '' ? '#7c3aed' : '#374151', fontWeight: selectedKasaID === '' ? 600 : 400, cursor: 'pointer', background: selectedKasaID === '' ? '#f5f3ff' : 'transparent' }}
                                                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
                                                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = selectedKasaID === '' ? '#f5f3ff' : 'transparent'; }}
                                                >{t('label.tumu', 'Tümü')}</div>
                                                {kasalar
                                                    .filter(k => !kasaSearch || k.kasaAdi.toLowerCase().includes(kasaSearch.toLowerCase()))
                                                    .map(k => (
                                                        <div
                                                            key={k.kasaID}
                                                            onMouseDown={() => { setSelectedKasaID(String(k.kasaID)); setKasaSearch(''); setShowKasaDropdown(false); }}
                                                            style={{ padding: '8px 12px', fontSize: '0.8125rem', color: String(k.kasaID) === selectedKasaID ? '#7c3aed' : '#374151', fontWeight: String(k.kasaID) === selectedKasaID ? 600 : 400, cursor: 'pointer', background: String(k.kasaID) === selectedKasaID ? '#f5f3ff' : 'transparent', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
                                                            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = String(k.kasaID) === selectedKasaID ? '#f5f3ff' : 'transparent'; }}
                                                        >
                                                            <span style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 3, fontSize: '0.68rem', fontWeight: 600, marginRight: 5, background: k.kasaTuru === 'Nakit' ? '#fef9c3' : '#dbeafe', color: k.kasaTuru === 'Nakit' ? '#854d0e' : '#1d4ed8', border: k.kasaTuru === 'Nakit' ? '1px solid #fde047' : '1px solid #93c5fd' }}>{k.kasaTuru}</span>
                                                            {k.kasaAdi}
                                                        </div>
                                                    ))
                                                }
                                                {kasalar.filter(k => !kasaSearch || k.kasaAdi.toLowerCase().includes(kasaSearch.toLowerCase())).length === 0 && (
                                                    <div style={{ padding: '10px 12px', fontSize: '0.8125rem', color: '#9ca3af', textAlign: 'center' }}>{t('msg.sonucBulunamadi', 'Sonuç bulunamadı')}</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {/* Başlangıç Tarihi */}
                                    <div>
                                        <label style={ekstreFilterLabelStyle}>{t('label.baslangicTarihi', 'Başlangıç Tarihi')}</label>
                                        <div style={{width:'100%'}}>
                                            <TrDateInput
                                                style={ekstreFilterInputStyle}
                                                value={kasaEkstreBaslangic}
                                                onChange={e => setKasaEkstreBaslangic(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    {/* Bitiş Tarihi */}
                                    <div>
                                        <label style={ekstreFilterLabelStyle}>{t('label.bitisTarihi', 'Bitiş Tarihi')}</label>
                                        <div style={{width:'100%'}}>
                                            <TrDateInput
                                                style={ekstreFilterInputStyle}
                                                value={kasaEkstreBitis}
                                                onChange={e => setKasaEkstreBitis(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <button onClick={handleKasaEkstreTemizle} style={{
                                        padding: '10px 24px', borderRadius: '10px', border: '1px solid #d1d5db',
                                        background: '#fff', color: '#475569', fontWeight: 600, fontSize: '0.85rem',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                                    }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg> {t('btn.temizle', 'Temizle')}
                                    </button>
                                    <button onClick={handleKasaEkstreOlustur} style={{
                                        padding: '9px 20px', borderRadius: '8px', border: 'none',
                                        background: '#7c3aed', color: '#fff',
                                        fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 1px 3px rgba(124,58,237,0.3)'
                                    }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> {t('btn.raporOlustur', 'Rapor Oluştur')}
                                    </button>
                                </div>
                            </div>

                            {/* Loading */}
                            {kasaEkstreLoading && (
                                <div style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                    <p style={{ fontWeight: 600 }}>{t('rapor.olusturuluyor', 'Rapor oluşturuluyor...')}</p>
                                </div>
                            )}

                            {/* Rapor İçeriği */}
                            {kasaEkstreReady && !kasaEkstreLoading && (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h5 style={{ margin: 0, fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/></svg>
                                            {t('rapor.kasaEkstre.hareketler', 'Kasa / Banka Hareketleri')}
                                        </h5>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button onClick={handleKasaEkstrePdfExport} style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: '#ef4444', color: '#fff', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', boxShadow: '0 1px 3px rgba(239,68,68,0.3)' }}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                                {t('btn.pdfIndir', 'PDF İndir')}
                                            </button>
                                            <button onClick={handleKasaEkstreExcelExport} style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: '#16a34a', color: '#fff', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', boxShadow: '0 1px 3px rgba(22,163,74,0.3)' }}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
                                                {t('btn.excelIndir', 'Excel İndir')}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Özet Kartları */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' }}>
                                        <div style={ekstreSummaryCard}>
                                            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('rapor.toplamGiris', 'Toplam Giriş')}</div>
                                            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>₺{formatCurrency(kasaToplamGiris)}</div>
                                        </div>
                                        <div style={ekstreSummaryCard}>
                                            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('rapor.toplamCikis', 'Toplam Çıkış')}</div>
                                            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#ef4444', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>₺{formatCurrency(kasaToplamCikis)}</div>
                                        </div>
                                        <div style={ekstreSummaryCard}>
                                            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('rapor.netBakiye', 'Net Bakiye')}</div>
                                            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: kasaNetBakiye >= 0 ? '#10b981' : '#ef4444', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>₺{formatCurrency(Math.abs(kasaNetBakiye))}</div>
                                        </div>
                                    </div>

                                    {/* Tablo */}
                                    {kasaEkstreWithBakiye.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '48px', color: '#94a3b8', background: '#fff', borderRadius: '16px', border: '2px dashed #e2e8f0' }}>
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                            <p style={{ margin: 0, fontWeight: 600, color: '#475569' }}>{t('msg.kayitYok', 'Seçilen kriterlere uygun kayıt bulunamadı')}</p>
                                        </div>
                                    ) : (
                                        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
                                            <div style={{ overflowX: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                                    <thead>
                                                        <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                                            <th style={thStyle}>{t('label.tarih')}</th>
                                                            <th style={thStyle}>{t('label.kasaBanka', 'Kasa/Banka')}</th>
                                                            <th style={thStyle}>{t('label.cari', 'Cari')}</th>
                                                            <th style={thStyle}>{t('label.aciklama')}</th>
                                                            <th style={thStyle}>{t('label.tur')}</th>
                                                            <th style={{ ...thStyle, textAlign: 'right' }}>{t('label.giris', 'Giriş (+)')}</th>
                                                            <th style={{ ...thStyle, textAlign: 'right' }}>{t('label.cikis', 'Çıkış (-)')}</th>
                                                            <th style={{ ...thStyle, textAlign: 'right' }}>{t('label.bakiye', 'Bakiye')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginatedKasaEkstre.map(o => (
                                                            <tr key={o.hareketID}
                                                                style={{ transition: 'background 0.15s' }}
                                                                onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
                                                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                                            >
                                                                <td style={tdStyle}>{new Date(o.tarih).toLocaleDateString('tr-TR')}</td>
                                                                <td style={tdStyle}>
                                                                    {o.kasa ? (
                                                                        <span>
                                                                            <span style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 3, fontSize: '0.68rem', fontWeight: 600, marginRight: 5, background: o.kasa.kasaTuru === 'Nakit' ? '#fef9c3' : '#dbeafe', color: o.kasa.kasaTuru === 'Nakit' ? '#854d0e' : '#1d4ed8', border: o.kasa.kasaTuru === 'Nakit' ? '1px solid #fde047' : '1px solid #93c5fd' }}>{o.kasa.kasaTuru}</span>
                                                                            {o.kasa.kasaAdi}
                                                                        </span>
                                                                    ) : '-'}
                                                                </td>
                                                                <td style={{ ...tdStyle, fontWeight: 600, color: '#0f172a' }}>{o.cari?.adiSoyadi || '-'}</td>
                                                                <td style={tdStyle}>{o.aciklama || o.kategori?.kategoriAdi || '-'}</td>
                                                                <td style={tdStyle}>
                                                                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, background: o.islemTuru === 'Giris' ? '#d1fae5' : '#fee2e2', color: o.islemTuru === 'Giris' ? '#065f46' : '#991b1b', border: o.islemTuru === 'Giris' ? '1px solid #a7f3d0' : '1px solid #fca5a5' }}>
                                                                        {o.islemTuru === 'Giris' ? t('label.girisUpper', 'GİRİŞ') : t('label.cikisUpper', 'ÇIKIŞ')}
                                                                    </span>
                                                                </td>
                                                                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#10b981' }}>
                                                                    {o.rowGiris != null ? `₺${formatCurrency(o.rowGiris)}` : '-'}
                                                                </td>
                                                                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#ef4444' }}>
                                                                    {o.rowCikis != null ? `₺${formatCurrency(o.rowCikis)}` : '-'}
                                                                </td>
                                                                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: o.bakiye >= 0 ? '#10b981' : '#ef4444' }}>
                                                                    ₺{formatCurrency(Math.abs(o.bakiye))}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {kasaEkstreWithBakiye.length > kasaEkstrePerPage && renderPagination(kasaEkstrePage, kasaEkstreTotalPages, setKasaEkstrePage)}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Henüz rapor oluşturulmadı */}
                            {!kasaEkstreReady && !kasaEkstreLoading && (
                                <div style={{
                                    textAlign: 'center', padding: '60px 20px', color: '#94a3b8',
                                    background: '#fff', borderRadius: '16px', border: '2px dashed #e2e8f0'
                                }}>
                                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/></svg>
                                    <p style={{ margin: 0, fontWeight: 600, color: '#475569', fontSize: '1rem' }}>
                                        {t('rapor.filtreSecin', 'Filtreleri seçin ve "Rapor Oluştur" butonuna tıklayın')}
                                    </p>
                                    <p style={{ margin: '8px 0 0', color: '#94a3b8', fontSize: '0.85rem' }}>
                                        {t('rapor.kasaEkstreIpucu', 'Kasa veya banka hesabı seçerek ekstre oluşturabilirsiniz')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* PROJE GELİR GİDER MODAL */}
            {showModal && (
                <div className="raporlar-modal-backdrop" style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
                    padding: '20px', overflowY: 'auto'
                }}>
                    <div className="raporlar-modal-box" style={{
                        background: '#f8fafc', borderRadius: '20px', width: '100%', maxWidth: '1200px',
                        maxHeight: '95vh', overflowY: 'auto', position: 'relative'
                    }}>
                        {/* Modal Header */}
                        <div className="raporlar-modal-sticky-header" style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '20px 28px', borderBottom: '1px solid #e2e8f0', background: '#fff',
                            borderRadius: '20px 20px 0 0', position: 'sticky', top: 0, zIndex: 10
                        }}>
                            <h3 style={{ margin: 0, fontWeight: 700, color: '#0f172a', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, background: '#fff7ed', border: '1px solid #fed7aa' }}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#cd6906" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                                </span>
                                {t('rapor.projeKarZararTitle', 'Proje Gelir-Gider ve Kâr-Zarar Durum Raporu')}
                            </h3>
                            <button className="raporlar-modal-close-btn" onClick={() => setShowModal(false)} style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: '#94a3b8', padding: '4px 8px', borderRadius: '8px', display: 'flex', alignItems: 'center'
                            }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        </div>

                        <div style={{ padding: '24px 28px' }}>
                            {/* Filtreler */}
                            <div className="raporlar-filter-section" style={{
                                background: '#fff', borderRadius: '12px', padding: '20px 24px',
                                marginBottom: '24px', border: '1px solid #e5e7eb'
                            }}>
                                <h5 style={{ margin: '0 0 16px', fontWeight: 600, color: '#374151', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 7 }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
                                    {t('rapor.raporFiltreleri', 'Rapor Filtreleri')}
                                </h5>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={filterLabelStyle}>
                                            {t('label.baslangicTarihi', 'Başlangıç Tarihi')}
                                            <label style={{ marginLeft: 10, fontSize: '0.78rem', fontWeight: 500, color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                                                <input type="checkbox" checked={modalTarihTumu} onChange={e => setModalTarihTumu(e.target.checked)} style={{ cursor: 'pointer' }} />
                                                {t('label.tumu', 'Tümü')}
                                            </label>
                                        </label>
                                        <input
                                            type="text"
                                            value={modalBaslangicDisplay}
                                            placeholder="gg.aa.yyyy"
                                            maxLength={10}
                                            disabled={modalTarihTumu}
                                            onChange={e => {
                                                let v = e.target.value.replace(/[^\d.]/g, '');
                                                if (v.length === 2 && !v.includes('.') && modalBaslangicDisplay.length < 3) v += '.';
                                                if (v.length === 5 && v.split('.').length === 2 && modalBaslangicDisplay.length < 6) v += '.';
                                                setModalBaslangicDisplay(v);
                                                if (/^\d{2}\.\d{2}\.\d{4}$/.test(v)) setModalBaslangic(v.split('.').reverse().join('-'));
                                            }}
                                            style={{ ...filterInputStyle, opacity: modalTarihTumu ? 0.4 : 1, pointerEvents: modalTarihTumu ? 'none' : 'auto' }} />
                                    </div>
                                    <div>
                                        <label style={filterLabelStyle}>{t('label.bitisTarihi', 'Bitiş Tarihi')}</label>
                                        <input
                                            type="text"
                                            value={modalBitisDisplay}
                                            placeholder="gg.aa.yyyy"
                                            maxLength={10}
                                            disabled={modalTarihTumu}
                                            onChange={e => {
                                                let v = e.target.value.replace(/[^\d.]/g, '');
                                                if (v.length === 2 && !v.includes('.') && modalBitisDisplay.length < 3) v += '.';
                                                if (v.length === 5 && v.split('.').length === 2 && modalBitisDisplay.length < 6) v += '.';
                                                setModalBitisDisplay(v);
                                                if (/^\d{2}\.\d{2}\.\d{4}$/.test(v)) setModalBitis(v.split('.').reverse().join('-'));
                                            }}
                                            style={{ ...filterInputStyle, opacity: modalTarihTumu ? 0.4 : 1, pointerEvents: modalTarihTumu ? 'none' : 'auto' }} />
                                    </div>
                                    <div>
                                        <label style={filterLabelStyle}>{t('label.proje', 'Proje')}</label>
                                        <select value={selectedProjeID} onChange={e => setSelectedProjeID(e.target.value)}
                                            style={filterInputStyle}>
                                            <option value="">{t('label.tumProjeler', 'Tüm Projeler')}</option>
                                            {projeler.map(p => (
                                                <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={filterLabelStyle}>{t('label.raporTuru', 'Rapor Türü')}</label>
                                        <select value={yonFiltre} onChange={e => setYonFiltre(e.target.value as 'Tümü' | 'Gelir' | 'Gider')}
                                            style={filterInputStyle}>
                                            <option value="Tümü">{t('label.tumu', 'Tümü')}</option>
                                            <option value="Gelir">{t('odeme.gelir')}</option>
                                            <option value="Gider">{t('odeme.gider')}</option>
                                        </select>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <button onClick={handleTemizle} style={{
                                        padding: '10px 24px', borderRadius: '10px', border: '1px solid #d1d5db',
                                        background: '#fff', color: '#475569', fontWeight: 600, fontSize: '0.85rem',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                                    }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg> {t('btn.temizle', 'Temizle')}
                                    </button>
                                    <button onClick={handleRaporOlustur} style={{
                                        padding: '9px 20px', borderRadius: '8px', border: 'none',
                                        background: '#cd6906', color: '#fff',
                                        fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 1px 3px rgba(205,105,6,0.3)'
                                    }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> {t('btn.raporOlustur', 'Rapor Oluştur')}
                                    </button>
                                </div>
                            </div>

                            {/* Loading */}
                            {modalLoading && (
                                <div style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                    <p style={{ fontWeight: 600 }}>{t('rapor.olusturuluyor', 'Rapor oluşturuluyor...')}</p>
                                </div>
                            )}

                            {/* Rapor İçeriği */}
                            {raporReady && !modalLoading && (
                                <>
                                    {/* PDF Butonu */}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                                        <button onClick={handleProjeRaporPdfExport} style={{
                                            padding: '8px 18px', borderRadius: '8px', border: '1px solid #e5e7eb',
                                            background: '#fff', color: '#374151', fontWeight: 600, fontSize: '0.82rem',
                                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                                        }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                            {t('btn.pdfIndir3Sayfa', 'PDF İndir (3 Sayfa)')}
                                        </button>
                                    </div>

                                    {filteredSozlesmeler.length === 0 && giderler.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '48px', color: '#94a3b8', background: '#fff', borderRadius: '12px', border: '2px dashed #e2e8f0' }}>
                                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                            <p style={{ margin: 0, fontWeight: 600, color: '#475569' }}>{t('msg.kayitYok', 'Seçilen kriterlere uygun kayıt bulunamadı')}</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* ===== BÖLÜM 1: ÖZET KARTLAR ===== */}
                                            <div style={{ marginBottom: '32px' }}>
                                                {/* Başlık + meta */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                                                    <span style={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase' as const, color: '#0f172a' }}>{t('menu.ozet')}</span>
                                                    <span style={{ fontSize: '0.73rem', color: '#94a3b8', background: '#f8fafc', padding: '2px 10px', borderRadius: 20, border: '1px solid #e2e8f0' }}>
                                                        {selectedProjeID ? (projeler.find(p => p.projeID === parseInt(selectedProjeID))?.projeAdi || '') : t('label.tumProjeler', 'Tüm Projeler')}
                                                        {!modalTarihTumu && ` • ${new Date(modalBaslangic).toLocaleDateString('tr-TR')} – ${new Date(modalBitis).toLocaleDateString('tr-TR')}`}
                                                    </span>
                                                </div>

                                                {/* 3 Kart Grid */}
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>

                                                    {/* KART 1: Gelir Özeti */}
                                                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 20px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                                                            <div style={{ width: 30, height: 30, borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                                                            </div>
                                                            <span style={{ fontWeight: 700, fontSize: '0.75rem', color: '#15803d', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>{t('rapor.gelirOzeti', 'Gelir Özeti')}</span>
                                                        </div>
                                                        {/* Toplam Gelir */}
                                                        <div style={{ marginBottom: 12 }}>
                                                            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 2 }}>{t('rapor.toplamGelir', 'Toplam Gelir')}</div>
                                                            <div style={{ fontSize: '1.45rem', fontWeight: 800, color: '#111827', fontVariantNumeric: 'tabular-nums' as const, letterSpacing: '-0.02em' }}>₺{formatCurrency(toplamGenelGelir)}</div>
                                                        </div>
                                                        {/* Gelir kayıt sayısı */}
                                                        <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>{t('rapor.gelirIslemleri', 'Gelir İşlemleri')}</span>
                                                                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#15803d' }}>{gelirler.length} {t('label.kayit', 'kayıt')}</span>
                                                            </div>
                                                        </div>
                                                        {/* Tahsilat rozeti + progress bar */}
                                                        <div style={{ marginTop: 14, background: '#f0fdf4', borderRadius: 10, padding: '10px 12px', border: '1px solid #dcfce7' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                                                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#15803d' }}>{t('rapor.gerceklesenTahsilat', 'Gerçekleşen Tahsilat')}</span>
                                                                <span style={{ fontSize: '0.7rem', fontWeight: 700, background: '#dcfce7', color: '#15803d', padding: '1px 8px', borderRadius: 20, border: '1px solid #86efac' }}>%{tahsilatOrani.toFixed(1)}</span>
                                                            </div>
                                                            <div style={{ height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
                                                                <div style={{ height: '100%', width: `${Math.min(tahsilatOrani, 100)}%`, background: '#22c55e', borderRadius: 999, transition: 'width 0.4s ease' }} />
                                                            </div>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                                                                <span style={{ fontSize: '0.7rem', color: '#4ade80' }}>₺{formatCurrency(tahsilEdilen)} {t('rapor.tahsil', 'tahsil')}</span>
                                                                <span style={{ fontSize: '0.7rem', color: '#f59e0b' }}>₺{formatCurrency(bekleyenGelir)} {t('rapor.bekleyen', 'bekleyen')}</span>
                                                            </div>
                                                        </div>

                                                    </div>

                                                    {/* KART 2: Gider Özeti */}
                                                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 20px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                                                            <div style={{ width: 30, height: 30, borderRadius: 8, background: '#fff1f2', border: '1px solid #fecdd3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
                                                            </div>
                                                            <span style={{ fontWeight: 700, fontSize: '0.75rem', color: '#b91c1c', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>{t('rapor.giderOzeti', 'Gider Özeti')}</span>
                                                        </div>
                                                        {/* Toplam Gider */}
                                                        <div style={{ marginBottom: 12 }}>
                                                            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 2 }}>{t('rapor.toplamGiderler', 'Toplam Giderler')}</div>
                                                            <div style={{ fontSize: '1.45rem', fontWeight: 800, color: '#b91c1c', fontVariantNumeric: 'tabular-nums' as const, letterSpacing: '-0.02em' }}>₺{formatCurrency(toplamGider)}</div>
                                                        </div>
                                                        {/* Proje dağılımı */}
                                                        {giderProjeArr.length > 0 && (
                                                            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
                                                                {giderProjeArr.map(([projeAdi, tutar]) => (
                                                                    <div key={projeAdi} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <span style={{ fontSize: '0.75rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, maxWidth: '60%' }}>• {projeAdi}</span>
                                                                        <span style={{ fontSize: '0.78rem', color: '#374151', fontVariantNumeric: 'tabular-nums' as const }}>₺{formatCurrency(tutar)}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {/* Ödenen / Kalan */}
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>{t('rapor.odenenGiderler', 'Ödenen Giderler')}</span>
                                                                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', fontVariantNumeric: 'tabular-nums' as const }}>₺{formatCurrency(giderOdenen)}</span>
                                                            </div>
                                                        </div>
                                                        {/* Kalan borç */}
                                                        <div style={{ marginTop: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '7px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <span style={{ fontSize: '0.75rem', color: '#78350f', fontWeight: 600 }}>{t('rapor.kalanBorc', 'Kalan Borç')}</span>
                                                            <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#a16207', fontVariantNumeric: 'tabular-nums' as const }}>₺{formatCurrency(giderKalanBorc)}</span>
                                                        </div>
                                                    </div>

                                                    {/* KART 3: Proje Sağlık */}
                                                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 20px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                                                            <div style={{ width: 30, height: 30, borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                                                            </div>
                                                            <span style={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>{t('rapor.projeSagligi', 'Proje Sağlığı')}</span>
                                                        </div>

                                                        {/* Kâr / Zarar */}
                                                        <div style={{ marginBottom: 10 }}>
                                                            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 4 }}>
                                                                {projeKar >= 0 ? t('rapor.netDonemKari', 'Net Dönem Kârı') : t('rapor.butceAcigi', 'Bütçe Açığı (Zarar Durumu)')}
                                                            </div>
                                                            {projeKar < 0 ? (
                                                                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                                                    <span style={{ fontWeight: 900, fontSize: '1.2rem', color: '#b91c1c', fontVariantNumeric: 'tabular-nums' as const }}>₺{formatCurrency(projeKar)}</span>
                                                                </div>
                                                            ) : (
                                                                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 12px' }}>
                                                                    <span style={{ fontWeight: 900, fontSize: '1.2rem', color: '#15803d', fontVariantNumeric: 'tabular-nums' as const }}>₺{formatCurrency(projeKar)}</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Net Nakit Pozisyonu */}
                                                        <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
                                                            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 4 }}>{t('rapor.netNakitPozisyonu', 'Net Nakit Pozisyonu')}</div>
                                                            <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: 6 }}>{t('rapor.netNakitAciklama', 'Gerçekleşen Tahsilat − Ödenen Gider')}</div>
                                                            {netNakitPozisyonu < 0 ? (
                                                                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                                                    <span style={{ fontWeight: 800, fontSize: '1.05rem', color: '#b91c1c', fontVariantNumeric: 'tabular-nums' as const }}>₺{formatCurrency(netNakitPozisyonu)}</span>
                                                                </div>
                                                            ) : (
                                                                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 12px' }}>
                                                                    <span style={{ fontWeight: 800, fontSize: '1.05rem', color: '#15803d', fontVariantNumeric: 'tabular-nums' as const }}>₺{formatCurrency(netNakitPozisyonu)}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* ===== BÖLÜM 2: GELİR LİSTESİ (tüm GEL- işlemleri) ===== */}
                                            <div className="rapor-sayfa-gecisi" style={{ paddingTop: '28px', borderTop: '3px solid #15803d', marginBottom: '36px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, borderLeft: '4px solid #15803d', paddingLeft: 12 }}>
                                                    <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#14532d', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>{t('rapor.gelirListesi', 'Gelir Listesi')}</span>
                                                    <span style={{ fontSize: '0.73rem', color: '#6b7280', background: '#f0fdf4', padding: '1px 8px', borderRadius: 20, border: '1px solid #bbf7d0' }}>{gelirler.length} {t('label.kayit', 'kayıt')}</span>
                                                    <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: '0.9rem', color: '#15803d' }}>₺{formatCurrency(toplamGelir)}</span>
                                                </div>
                                                {gelirler.length === 0 ? (
                                                    <div style={{ textAlign: 'center', padding: '28px', color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 8 }}>{t('msg.gelirKaydiYok', 'Gelir kaydı bulunamadı')}</div>
                                                ) : (
                                                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                                                            <thead>
                                                                <tr style={{ background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
                                                                    {(['#', t('label.tarih'), t('label.proje', 'Proje'), t('label.daire', 'Daire'), t('label.musteriCari', 'Müşteri / Cari'), t('label.aciklama')] as const).map((h, i) => (
                                                                        <th key={i} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.73rem', textTransform: 'uppercase' as const, letterSpacing: '0.04em', width: i === 0 ? 36 : undefined }}>{h}</th>
                                                                    ))}
                                                                    <th style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, color: '#374151', fontSize: '0.73rem', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{t('label.tutar')}</th>
                                                                    <th style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: '0.73rem', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{t('label.durum')}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {paginatedGelirler.map((o, i) => (
                                                                    <tr key={o.odemeID} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #f1f5f9' }}>
                                                                        <td style={{ padding: '8px 12px', textAlign: 'center', color: '#9ca3af', fontSize: '0.78rem' }}>{(gelirPage - 1) * perPage + i + 1}</td>
                                                                        <td style={{ padding: '8px 12px', color: '#6b7280', fontSize: '0.8rem', whiteSpace: 'nowrap' as const }}>{new Date(o.islemTarihi).toLocaleDateString('tr-TR')}</td>
                                                                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#111827' }}>{o.ilgiliProje?.projeAdi || o.sozlesme?.projeAdi || '-'}</td>
                                                                        <td style={{ padding: '8px 12px', color: '#374151' }}>{o.sozlesme?.daireNo || '-'}</td>
                                                                        <td style={{ padding: '8px 12px', color: '#374151' }}>{o.cari?.adiSoyadi || '-'}</td>
                                                                        <td style={{ padding: '8px 12px', color: '#6b7280', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{o.aciklama || '-'}</td>
                                                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#15803d', fontVariantNumeric: 'tabular-nums' as const }}>₺{formatCurrency(o.miktar)}</td>
                                                                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                                            {o.odemeDurumu === 'Odendi' ? (
                                                                                <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}>{t('durum.tahsilEdildi', 'Tahsil Edildi')}</span>
                                                                            ) : o.odemeDurumu === 'Iptal' ? (
                                                                                <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600, background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>{t('durum.iptal')}</span>
                                                                            ) : (o.vadeTarihi && new Date(o.vadeTarihi) <= bugun) ? (
                                                                                <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>{t('durum.gecikmis', 'Gecikmiş')}</span>
                                                                            ) : (
                                                                                <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>{t('durum.bekliyor')}</span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot>
                                                                <tr style={{ background: '#dcfce7', borderTop: '2px solid #86efac' }}>
                                                                    <td colSpan={6} style={{ padding: '9px 12px', fontWeight: 700, color: '#14532d', textAlign: 'right', fontSize: '0.85rem' }}>{t('label.toplam', 'Toplam')}</td>
                                                                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 800, color: '#111827', fontVariantNumeric: 'tabular-nums' as const, fontSize: '0.92rem' }}>₺{formatCurrency(toplamGelir)}</td>
                                                                    <td />
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </div>
                                                )}
                                                {gelirler.length > perPage && renderPagination(gelirPage, gelirTotalPages, setGelirPage)}
                                            </div>

                                            {/* ===== BÖLÜM 3: GİDER LİSTESİ ===== */}
                                            <div className="rapor-sayfa-gecisi" style={{ paddingTop: '28px', borderTop: '3px solid #b91c1c' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, borderLeft: '4px solid #b91c1c', paddingLeft: 12 }}>
                                                    <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#7f1d1d', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>{t('rapor.giderListesi', 'Gider Listesi')}</span>
                                                    <span style={{ fontSize: '0.73rem', color: '#6b7280', background: '#fff1f2', padding: '1px 8px', borderRadius: 20, border: '1px solid #fecdd3' }}>{giderler.length} {t('label.kayit', 'kayıt')}</span>
                                                    <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: '0.9rem', color: '#b91c1c' }}>₺{formatCurrency(toplamGider)}</span>
                                                </div>
                                                {giderler.length === 0 ? (
                                                    <div style={{ textAlign: 'center', padding: '28px', color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 8 }}>{t('msg.giderKaydiYok', 'Gider kaydı bulunamadı')}</div>
                                                ) : (
                                                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                                                            <thead>
                                                                <tr style={{ background: '#fff1f2', borderBottom: '1px solid #fecdd3' }}>
                                                                    {([t('label.tarih'), t('label.proje', 'Proje'), t('label.kategori', 'Kategori'), t('label.aciklama'), t('label.tedarikci', 'Tedarikçi')] as const).map((h, i) => (
                                                                        <th key={i} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.73rem', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{h}</th>
                                                                    ))}
                                                                    <th style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600, color: '#374151', fontSize: '0.73rem', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{t('label.tutar')}</th>
                                                                    <th style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: '0.73rem', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>{t('label.durum')}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {paginatedGiderler.map((o, i) => {
                                                                    const durum = getDurumBadge(o);
                                                                    return (
                                                                        <tr key={o.odemeID} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                                                                            <td style={{ padding: '11px 12px', color: '#6b7280', fontSize: '0.8rem' }}>{new Date(o.islemTarihi).toLocaleDateString('tr-TR')}</td>
                                                                            <td style={{ padding: '11px 12px', fontWeight: 600, color: '#111827' }}>{o.ilgiliProje?.projeAdi || o.sozlesme?.projeAdi || '-'}</td>
                                                                            <td style={{ padding: '11px 12px' }}>
                                                                                <span style={{ padding: '2px 9px', borderRadius: 9999, fontSize: '0.68rem', fontWeight: 600, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', whiteSpace: 'nowrap' as const }}>
                                                                                    {(o.hizmetUrunAdi || 'Diğer').toUpperCase()}
                                                                                </span>
                                                                            </td>
                                                                            <td style={{ padding: '11px 12px', color: '#374151', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{o.aciklama || '-'}</td>
                                                                            <td style={{ padding: '11px 12px', color: '#374151' }}>{o.cari?.adiSoyadi || '-'}</td>
                                                                            <td style={{ padding: '11px 12px', textAlign: 'right', fontWeight: 700, color: '#b91c1c', fontVariantNumeric: 'tabular-nums' as const }}>₺{formatCurrency(o.miktar)}</td>
                                                                            <td style={{ padding: '11px 12px', textAlign: 'center' }}>
                                                                                {o.odemeDurumu === 'Odendi' ? (
                                                                                    <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}>{t('durum.odendi', 'Ödendi')}</span>
                                                                                ) : o.odemeDurumu === 'Iptal' ? (
                                                                                    <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600, background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>{t('durum.iptal')}</span>
                                                                                ) : (o.vadeTarihi && new Date(o.vadeTarihi) <= bugun) ? (
                                                                                    <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>{t('durum.gecikmis', 'Gecikmiş')}</span>
                                                                                ) : (
                                                                                    <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>{t('durum.bekliyor')}</span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                            <tfoot>
                                                                <tr style={{ background: '#fee2e2', borderTop: '2px solid #fca5a5' }}>
                                                                    <td colSpan={5} style={{ padding: '9px 12px', fontWeight: 700, color: '#7f1d1d', textAlign: 'right', fontSize: '0.85rem' }}>{t('rapor.toplamGider', 'Toplam Gider')}</td>
                                                                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 800, color: '#b91c1c', fontVariantNumeric: 'tabular-nums' as const, fontSize: '0.92rem' }}>₺{formatCurrency(toplamGider)}</td>
                                                                    <td></td>
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </div>
                                                )}
                                                {giderler.length > perPage && renderPagination(giderPage, giderTotalPages, setGiderPage)}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            {/* Henüz rapor oluşturulmadı */}
                            {!raporReady && !modalLoading && (
                                <div style={{
                                    textAlign: 'center', padding: '60px 20px', color: '#94a3b8',
                                    background: '#fff', borderRadius: '16px', border: '2px dashed #e2e8f0'
                                }}>
                                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    <p style={{ margin: 0, fontWeight: 600, color: '#475569', fontSize: '1rem' }}>
                                        {t('rapor.filtreSecin', 'Filtreleri seçin ve "Rapor Oluştur" butonuna tıklayın')}
                                    </p>
                                    <p style={{ margin: '8px 0 0', color: '#94a3b8', fontSize: '0.85rem' }}>
                                        {t('rapor.projeRaporIpucu', 'Proje, tarih aralığı ve rapor türü seçerek analiz oluşturabilirsiniz')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Pagination bileşeni
const renderPagination = (currentPage: number, totalPages: number, setPage: React.Dispatch<React.SetStateAction<number>>) => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', padding: '16px 24px', borderTop: '1px solid #f1f5f9' }}>
        <button onClick={() => setPage(1)} disabled={currentPage === 1} style={pagBtnStyle(currentPage === 1)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></svg>
        </button>
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={pagBtnStyle(currentPage === 1)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
            .map((p, idx, arr) => (
                <React.Fragment key={p}>
                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                        <span style={{ padding: '6px 4px', color: '#94a3b8', fontSize: '0.78rem' }}>...</span>
                    )}
                    <button onClick={() => setPage(p)} style={{
                        padding: '6px 12px', borderRadius: '6px',
                        border: p === currentPage ? 'none' : '1px solid #e2e8f0',
                        background: p === currentPage ? '#cd6906' : '#fff',
                        color: p === currentPage ? '#fff' : '#475569',
                        cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600
                    }}>{p}</button>
                </React.Fragment>
            ))
        }
        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={pagBtnStyle(currentPage === totalPages)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
        <button onClick={() => setPage(totalPages)} disabled={currentPage === totalPages} style={pagBtnStyle(currentPage === totalPages)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></svg>
        </button>
    </div>
);

// Stiller
const filterLabelStyle: React.CSSProperties = {
    display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#0f172a', marginBottom: '6px'
};

const filterInputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: '10px',
    border: '1px solid #e2e8f0', fontSize: '0.88rem', outline: 'none',
    background: '#fff', boxSizing: 'border-box'
};

const thStyle: React.CSSProperties = {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 700,
    color: '#6b7280',
    fontSize: '0.6875rem',
    letterSpacing: '0.05em',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    background: '#f9fafb',
    borderTop: '1px solid #e5e7eb',
    borderBottom: '1px solid #e5e7eb',
    fontFamily: "'Inter', 'Roboto', sans-serif",
};

const tdStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
    fontSize: '0.875rem',
    letterSpacing: 0,
    fontFamily: "'Inter', 'Roboto', sans-serif",
};

const pagBtnStyle = (disabled: boolean): React.CSSProperties => ({
    padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0',
    background: disabled ? '#f8fafc' : '#fff', color: disabled ? '#cbd5e1' : '#475569',
    cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: 600
});

const gelirSummaryCardStyle: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
};

const ekstreSummaryCard: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
};

const ekstreFilterLabelStyle: React.CSSProperties = {
    display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#0f172a', marginBottom: '6px'
};

const ekstreFilterInputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: '10px',
    border: '1px solid #bfdbfe', fontSize: '0.88rem', outline: 'none',
    background: '#fff', boxSizing: 'border-box'
};

const ekstrePdfBtnStyle: React.CSSProperties = {
    padding: '8px 14px', borderRadius: '8px', border: '1px solid #e5e7eb',
    background: '#fff', color: '#374151', fontWeight: 500, fontSize: '0.8rem',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
};

const ekstreExcelBtnStyle: React.CSSProperties = {
    padding: '8px 14px', borderRadius: '8px', border: '1px solid #e5e7eb',
    background: '#fff', color: '#374151', fontWeight: 500, fontSize: '0.8rem',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
};

export default Raporlar;
